
import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { CardTab, CardType, ContentCard, HeaderTabs } from 'src/components/organisms/ContentCard';
import { Formik, FormikHelpers } from 'formik';
import type { FormikProps } from 'formik';
import SaveIcon from '@mui/icons-material/Save';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, Grid, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import TooltipField from 'src/components/atoms/TooltipField';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import { productPostRequest, productPutRequest } from 'src/utils/submit/submitProduct';
import useProductOptions from 'src/hooks/forms/useOptions';
import randomId from 'src/utils/randomId';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import { useSalesItemsOnProductsTable } from 'src/hooks/table/configs/useSalesItemsTable';
import FormHelper from 'src/utils/formHelper';
import { useProductForm } from 'src/hooks/forms/useForms';
import useTour from 'src/hooks/tours/useTour';
import CustomJoyride from 'src/components/organisms/CustomJoyride';
import { useProductSubmit } from 'src/hooks/forms/useProductSubmit';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { AiAutocompleteTest } from 'src/components/molecules/ai/AiAutocompleteTest';
import { useSelectSupplierProductsOnProduct } from 'src/hooks/table/configs/useSelectSupplierProductOnProduct';
import SelectTableDialog from 'src/components/organisms/tables/SelectTableDialog';
import { FormInfoButton } from 'src/components/atoms/buttons/FormInfoButton';
import { TESTING_FUNCTIONALITY } from 'src/utils/enviromentValues';
import { useFormDefaults } from 'src/hooks/forms/useFormDefaults';
import { FormDefaultsMenu } from 'src/components/molecules/formik/FormDefaultsMenu';

const productScheme = Yup.object().shape({
    Name: Yup.string()
        .required('Enter the item name as it should appear in lists and documents.'),

    ProductType: Yup.string()
        .required('Select a product type so the item is correctly categorized (e.g. Lamps, Bulbs, Accessories).'),

    Manufacturer: Yup.string()
        .required('Select the manufacturer that produces this product.'),

    ManufacturerProductId: Yup.string()
        .required('Enter the product ID used by the manufacturer (their article number).'),

    Status: Yup.string()
        .required('Select the current status for this product (Active, Inactive or Discontinued).'),

    ProductClass: Yup.string()
        .required('Select a product class for internal grouping and reporting.'),

    ValidFrom: Yup.date()
        .typeError('Enter a valid start date for when this product becomes active.')
        .required('Select the date from which this product should be treated as active.'),

    TARIC: Yup.number()
        .nullable()
        .typeError('Enter a valid TARIC number (digits only), or leave empty for now.'),

    CountryOfOrigin: Yup.string()
        .nullable(),

    ValidTo: Yup.date()
        .nullable()
        .typeError('Enter a valid end date or leave it empty if the product should be valid indefinitely.')
        .test(
            'is-after-valid-from',
            'Valid To must be later than Valid From.',
            function (value) {
                const { ValidFrom } = this.parent;

                if (!value || !ValidFrom) return true;

                const start = new Date(ValidFrom);
                const end = new Date(value);

                return end > start;
            }
        ),
});


const requiredProductFields = [
    'Name',
    'ProductType',
    'Manufacturer',
    'ManufacturerProductId',
    'Status',
    'ProductClass',
    'ValidFrom',
];

const defaultInitialValues: any = {
    Name: "",
    ProductId: "",
    Status: 2,
    IsPhysical: true,
    IsDigital: false,
    ProductDefinition: 1,
    Manufacturer: "",
    ProductLine: "",
    ProductClass: "",
    ProductSubClass: "",
    ProductType: "",
    CountryOfOrigin: "",
    ManufacturerProductId: "",
    ManufacturerAltProductId: "",
    ManufacturerProductName: "",
    ValidFrom: new Date().toISOString().split('T')[0],
    ValidTo: null,
    TARIC: "",
    IsCustomerSpecific: false,
    NumberOfIncludedItems: "",
    Created: "",
    Modified: "",
    SupplierProducts: [],
    SalesItems: [],
    isEditPage: "",
};

const testProductValues = [
    { Name: "Testprodukt Webclient " + randomId() },
    { ProductId: null },
    { Manufacturer: 2 },
    { ProductType: 2 },
    { ManufacturerProductId: "11" },
    { ManufacturerAltProductId: "" },
    { ManufacturerProductName: "Testprodukt ManName Webclient " + randomId() },
    { ProductClass: 2 },
    { IsPhysical: true },
    { TARIC: 1234567 },
    { ValidFrom: "2025-02-10T23:00:00Z" },
    { ValidTo: "" },
    { CountryOfOrigin: 1 }
];

function EditProductWithAiButtons() {
    const tourProps = useTour("EditProduct");
    const [editId, PageName, currentSubmitFunction, , isEditPage] = FormHelper.getEditPageConfig("Product", productPostRequest, productPutRequest);
    const scheme = productScheme;

    const [result, submitProduct, submitting] = useProductSubmit(currentSubmitFunction);
    const initialValues = useProductForm(editId, defaultInitialValues, result);

    const [productTypeOptions, productClassOptions, productSubClassOptions, manufacturerOptions, productLinesOptions, countryOptions] = useProductOptions();

    useRedirectOnCreate(result, editId, "ProductId", "/products/products/edit-test?id=");

    const [supplierCols, supplierSettings, linkedSupplierProducts, supplierSelector] = useSelectSupplierProductsOnProduct(initialValues);
    const [salesItemsColumns, salesItemsTableSettings] = useSalesItemsOnProductsTable(initialValues);

    const tabs: CardTab[] = [
        { value: 'Supplier Products', label: 'Supplier Products' },
        { value: 'Sales Items', label: 'Sales Items' },
        { value: 'System', label: 'System Info' }
    ];
    const [currentTab, setCurrentTab] = useState<string>('Supplier Products');

    const [highlightedFields, setHighlightedFields] = useState<string[]>([]);
    const addHighlightToField = (fieldName: string) => {
        setHighlightedFields(prev => [...prev, fieldName]);
        setTimeout(() => {
            setHighlightedFields(prev => prev.filter(n => n !== fieldName));
        }, 2000);
    };

    const { FormDefaultsListener, formDefaultsProps } = useFormDefaults({
        formKey: "EditProductForm",
        isEditPage,
        fields: {
          ProductType: {  },
          Manufacturer: {  },
          Status: { fallback: 2, clearValue: 2 },
          ProductClass: { },
        },
      });

    return (
        <>
            <CustomJoyride tourProps={tourProps} />
            <Formik
                initialValues={initialValues}
                validateOnBlur={true}
                validateOnChange={false}
                validationSchema={scheme}
                onSubmit={(values: any, formikHelpers: FormikHelpers<any>) => {
                    submitProduct(values, linkedSupplierProducts, formikHelpers);
                }
                }
                enableReinitialize
            >
                {(props): JSX.Element =>  (
                        <ProductLlmClipboardProvider
                            formikProps={props}
                            pageContext={{
                                entityName: "Product",
                                pageName: getPageNameText(PageName),
                                editId,
                                currentTab,
                                initialValues,
                            }}
                            productOptionsContext={{
                                productTypeOptions,
                                productClassOptions,
                                productSubClassOptions,
                                manufacturerOptions,
                                productLinesOptions,
                                countryOptions,
                            }}
                        >
                        <div>
                             <FormDefaultsListener formikProps={props} />
                            <PageHeader  {...{
                                title: PageName,
                                initialValues: initialValues,
                                subTitle: "Create a new Product",
                                components: [
                                    TESTING_FUNCTIONALITY && { component: <AiAutocompleteTest addHighlightToField={addHighlightToField} /> },
                                    { component: (<FormInfoButton formikProps={props} />), },
                                    { component: <ProductLlmCopyButton /> },
                                    { component: <ProductLlmPasteButton /> },
                                    { component: <ProductLlmAiConfigButton /> },
                                    { component: <ProductLlmAiRunButton /> },

                                    { component: <CustomButton isSubmitting={submitting} onClick={props.handleSubmit} title="Save" icon={<SaveIcon />} /> },
                                    { isInGroupedMenu: true, component: <FormDefaultsMenu formikProps={props} formDefaultsProps={formDefaultsProps} /> },

                                    { isInGroupedMenu: true, component: <CustomButton variant="text" title="New Product" icon={<AddTwoToneIcon />} actionUrl="/products/products/create" /> },
                                    { isInGroupedMenu: true, component: <CustomButton variant='text' title="Manage Product Lines" actionUrl="/products/product-lines" /> },
                                ],
                                alertData: { dataArray: (result ?? false) ? [result] : null }
                            }} />
                            <ContentCardsContainer >

                                <ContentCard {...{ cardId: "General Product info", md: 6 }}>
                                    <ProductLlmFormContainer formikProps={props}>
                                        <ProductLlmFormField fieldProps={{ name: "Name", className: "flash-highlight", label: "Item Name", md: 6.8 }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "Status", select: true, label: "Status", md: 4.8 }} formikProps={props}>
                                            <MenuItem value="1">Active</MenuItem>
                                            <MenuItem value="2">Inactive</MenuItem>
                                            <MenuItem value="3">Discontinued</MenuItem>
                                        </ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ValidFrom", date: true, label: "Valid From", md: 5.8 }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ValidTo", date: true, label: "Valid To", md: 5.8 }} formikProps={props}></ProductLlmFormField>

                                        <ProductLlmFormField fieldProps={{ name: "ProductType", className: highlightedFields.includes("ProductType") ? "flash-highlight active" : "flash-highlight", select: true, label: "Product Type", md: 12, tooltipText: "Product type should be set to match the category of the product. Eg. Lamps" }} formikProps={props}>
                                            {productTypeOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ProductClass", className: highlightedFields.includes("ProductClass") ? "flash-highlight active" : "flash-highlight", select: true, label: "Product Class", md: 6 }} formikProps={props}>
                                            {productClassOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ProductSubClass", className: highlightedFields.includes("ProductSubClass") ? "flash-highlight active" : "flash-highlight", select: true, label: "Subclass", md: 5.8 }} formikProps={props}>
                                            {(productSubClassOptions[props.values["ProductClass"]] == undefined) ?
                                                <MenuItem key={1} value={"unloaded"}></MenuItem>
                                                :
                                                productSubClassOptions[props.values["ProductClass"]]?.map((option) =>
                                                    (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ProductDefinition", select: true, label: "Sales Format", md: 5.8 }} formikProps={props}>
                                            <MenuItem value={1}>Physical</MenuItem>
                                            <MenuItem value={2}>Digital</MenuItem>
                                        </ProductLlmFormField>

                                        <ProductLlmFormField fieldProps={{ name: "IsCustomerSpecific", checkbox: true, label: "Customized", md: 2.8 }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "NumberOfIncludedItems", label: "Items Included", md: 2.8, type: "number" }} formikProps={props}></ProductLlmFormField>

                                    </ProductLlmFormContainer>
                                </ContentCard>

                                <ContentCard {...{ cardId: "Manufacturer", md: 6 }}>
                                    <ProductLlmFormContainer formikProps={props}>
                                        <ProductLlmFormField fieldProps={{ name: "Manufacturer", className: highlightedFields.includes("Manufacturer") ? "flash-highlight active" : "flash-highlight", select: true, label: "Manufacturer Name", md: 12 }} formikProps={props}>
                                            {manufacturerOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}

                                        </ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ManufacturerProductId", label: "Product Id", md: 5.8 }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ManufacturerAltProductId", label: "Alternative Product Id", md: 5.8 }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ManufacturerProductName", label: "Manufacturer Product Name", md: 12, tooltipText: "Can differ from Item Name, Is given by manufacturer" }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "ProductLine", select: true, label: "Product Line", md: 10.8 }} formikProps={props}>
                                            {productLinesOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </ProductLlmFormField>
                                        <Grid item md={1}
                                            sx={{
                                                alignSelf: "anchor-center"
                                            }}>
                                            <CustomButton iconButton={true} size='small' icon={<AddTwoToneIcon />} actionUrl="/products/product-lines/create" />
                                        </Grid>
                                        <ProductLlmFormField fieldProps={{ name: "TARIC", label: "TARIC", md: 12, warning: "TARIC is required to be able to export this product. Add a TARIC code before exporting.", tooltipText: <>View correct TARIC codes at: <a style={{ "color": "white" }} target={"_blank"} rel="noreferrer" href="https://ec.europa.eu/taxation_customs/dds2/taric/taric_consultation.jsp">Taric codes</a></> }} formikProps={props}></ProductLlmFormField>
                                        <ProductLlmFormField fieldProps={{ name: "CountryOfOrigin", select: true, label: "Country Of Origin", md: 12, warning: "Country of origin is required to export this product. Set the correct country before exporting.", }} formikProps={props}>
                                            {countryOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                        </ProductLlmFormField>
                                    </ProductLlmFormContainer>
                                </ContentCard>

                                <ContentCard {...{
                                    cardId: "TabContainer",
                                    contentPadding: 0,
                                    cardTypes: [CardType.tabContainer],
                                    cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} ></HeaderTabs>,
                                    components: [
                                        {
                                            isInGroupedMenu: false,
                                            component: <CustomButton target='blank' variant='contained'
                                                title="New Supplier Product"
                                                icon={<AddTwoToneIcon />} actionUrl={"/products/supplier-products/create?productId=" + editId} />
                                        },
                                        {
                                            isInGroupedMenu: false,
                                            component: <CustomButton target='blank' variant='contained'
                                                title="Link Supplier Product" icon={<AddLinkIcon />}
                                                onClick={() => supplierSelector.openSelector()} />

                                        },
                                    ],
                                }}>
                                    {currentTab === "Supplier Products" &&
                                        <ContentCard {...{ cardId: "Supplier Products", cardTypes: [CardType.tab], contentPadding: 0, }}>
                                            <SelectTableDialog {...supplierSelector} />
                                            <CustomDataGrid
                                                columns={supplierCols}
                                                tableSettings={supplierSettings}
                                            />
                                        </ContentCard>
                                    }
                                    {currentTab === "Sales Items" &&

                                        <ContentCard {...{ cardId: "Sales Items", cardTypes: [CardType.tab], contentPadding: 0 }}>


                                            <CustomDataGrid columns={salesItemsColumns} tableSettings={salesItemsTableSettings} />
                                        </ContentCard>

                                    }

                                    {currentTab === "System" &&
                                        <ContentCard {...{ cardId: "System Info", cardTypes: [CardType.tab], contentPadding: 2 }}>
                                            <ProductLlmFormContainer formikProps={props}>
                                                <ProductLlmFormField fieldProps={{ name: "Created", date: true, label: "Created", md: 5.8, disabled: true }} formikProps={props}></ProductLlmFormField>
                                                <ProductLlmFormField fieldProps={{ name: "Modified", date: true, label: "Modified", md: 5.8, disabled: true }} formikProps={props}></ProductLlmFormField>
                                            </ProductLlmFormContainer>
                                        </ContentCard>
                                    }
                                </ContentCard>

                            </ContentCardsContainer>
                        </div>
                        </ProductLlmClipboardProvider>
                    )
                }
            </Formik>

            <Footer />
        </>
    );
}

type ProductLlmOptionsContext = {
    productTypeOptions: any;
    productClassOptions: any;
    productSubClassOptions: any;
    manufacturerOptions: any;
    productLinesOptions: any;
    countryOptions: any;
};

type ProductLlmPageContext = {
    entityName: string;
    pageName: string;
    editId: any;
    currentTab: string;
    initialValues: any;
};

type ProductLlmChangedField = {
    fieldName: string;
    originalValue: any;
    pastedValue: any;
};

type ProductLlmChangedFields = Record<string, ProductLlmChangedField>;

type ProductLlmClipboardController = {
    copyPromptToClipboard: (event?: React.MouseEvent) => Promise<void>;
    pasteValuesFromClipboard: (event?: React.MouseEvent) => Promise<void>;
    applyLlmResponseText: (responseText: string) => Promise<ProductLlmApplyResult>;
    buildPrompt: () => string;
    revertField: (fieldName: string) => Promise<void>;
    copyButtonTitle: string;
    pasteButtonTitle: string;
    changedFields: ProductLlmChangedFields;
};

type ProductLlmApplyResult = 'applied' | 'no-fields' | 'no-changes';

type UseProductLlmClipboardArgs = {
    formikProps: FormikProps<any>;
    pageContext: ProductLlmPageContext;
    productOptionsContext: ProductLlmOptionsContext;
    additionalContext?: Record<string, unknown>;
};

type ProductLlmClipboardProviderProps = UseProductLlmClipboardArgs & {
    children: React.ReactNode;
};

const ProductLlmClipboardContext = React.createContext<ProductLlmClipboardController | null>(null);

const productLlmReadOnlyFields = [
    "Id",
    "ProductId",
    "Created",
    "Modified",
    "SupplierProducts",
    "SalesItems",
    "isEditPage",
];

const productLlmEditableFields = [
    "Name",
    "Status",
    "ValidFrom",
    "ValidTo",
    "ProductType",
    "ProductClass",
    "ProductSubClass",
    "ProductDefinition",
    "IsCustomerSpecific",
    "NumberOfIncludedItems",
    "Manufacturer",
    "ManufacturerProductId",
    "ManufacturerAltProductId",
    "ManufacturerProductName",
    "ProductLine",
    "TARIC",
    "CountryOfOrigin",
];

const productLlmContextFields = [
    "ProductId",
    ...productLlmEditableFields,
    "Created",
    "Modified",
    "isEditPage",
];

const commonLlmJsonReturnPrompt = `
Return only valid JSON. Do not wrap it in a codeblock. Do not add explanations.

Expected shape:
{
  "values": {
    "Name": "Updated item name",
    "ProductType": "value-from-available-options",
    "ManufacturerProductId": "manufacturer-article-number"
  }
}

Rules:
- Only include fields that should be changed.
- The user request is authoritative. When the user asks to fill, infer, correct, or update the form, return every editable field you can determine from the request, attachments, context, and available options.
- Do not return an empty "values" object for an actionable request. Return at least one editable field unless the user explicitly asks for no changes or the supplied information truly contains no usable product data.
- When creating a new product, include all required fields listed in requiredFields when you have enough data.
- The keys inside "values" must match the Formik field names exactly.
- Keep booleans as booleans, numbers as numbers, and strings as strings.
- Do not include read-only/system fields unless explicitly asked.
`.trim();

const commonLlmOptionsPrompt = `
When a field has available options, use the option value in the returned JSON, not the display label.
Things like Manufacturer, ProductType, ProductClass, ProductSubClass, ProductLine, CountryOfOrigin, ProductDefinition, and Status require IDs/option values, not labels; find those IDs in fieldOptions in the attached context data.
Compact option objects use this shape: { "Display label": value }.
ProductSubClass options are grouped by selected ProductClass value.
If the correct option is unclear, keep the existing value instead of guessing.
`.trim();

const productLlmPrompt = `
You are helping update a Product form.
Use the compact current form values, compact original initial values, available select options, required fields, related rows, and page context below.
Return a JSON object that can be pasted back into the form to update the product.

General product rules:
- ManufacturerProductId is the manufacturer's article/product number and is required.
- ManufacturerProductName is the manufacturer's display/name/description for the product and is not the same as ManufacturerProductId.
- ProductType should describe the product category as specifically as possible.
- Product Line is rarley used and should only be set when its obvious which product line the product belongs to. 
- ProductClass is an internal product grouping. ProductSubClass must belong to the selected ProductClass when used.
- TARIC and CountryOfOrigin are important for export readiness, but TARIC can be left empty when the correct code is not known.
`.trim();

function ProductLlmClipboardProvider({ children, ...hookArgs }: ProductLlmClipboardProviderProps) {
    const productLlmClipboard = useProductLlmClipboard(hookArgs);

    return (
        <ProductLlmClipboardContext.Provider value={productLlmClipboard}>
            {children}
        </ProductLlmClipboardContext.Provider>
    );
}

function useProductLlmClipboardContext() {
    const productLlmClipboard = useContext(ProductLlmClipboardContext);

    if (!productLlmClipboard) {
        throw new Error("ProductLlmClipboardContext is missing.");
    }

    return productLlmClipboard;
}

function ProductLlmCopyButton() {
    const productLlmClipboard = useProductLlmClipboardContext();

    return <CustomButton variant="text" title={productLlmClipboard.copyButtonTitle} icon={<ContentCopyIcon />} onClick={productLlmClipboard.copyPromptToClipboard} />;
}

function ProductLlmPasteButton() {
    const productLlmClipboard = useProductLlmClipboardContext();

    return <CustomButton variant="text" title={productLlmClipboard.pasteButtonTitle} icon={<ContentPasteIcon />} onClick={productLlmClipboard.pasteValuesFromClipboard} />;
}

function ProductLlmAiConfigButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Config" icon={<SettingsIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <ProductLlmAiConfigDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}

function ProductLlmAiRunButton() {
    const productLlmClipboard = useProductLlmClipboardContext();
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Run" icon={<AutoAwesomeIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <ProductLlmAiRunDialog
                open={open}
                onClose={() => setOpen(false)}
                buildPrompt={productLlmClipboard.buildPrompt}
                applyLlmResponseText={productLlmClipboard.applyLlmResponseText}
            />
        </>
    );
}

function useProductLlmClipboard({ formikProps, pageContext, productOptionsContext, additionalContext = {} }: UseProductLlmClipboardArgs): ProductLlmClipboardController {
    const [copyButtonTitle, setCopyButtonTitle] = useState<string>("Copy");
    const [pasteButtonTitle, setPasteButtonTitle] = useState<string>("Paste");
    const [changedFields, setChangedFields] = useState<ProductLlmChangedFields>({});

    const setTemporaryButtonTitle = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, temporaryTitle: string, defaultTitle: string) => {
        setter(temporaryTitle);
        window.setTimeout(() => setter(defaultTitle), 1800);
    }, []);

    const getPromptData = useCallback(() => {
        const fieldOptions = {
            ProductType: compactProductLabelValueOptions(productOptionsContext.productTypeOptions),
            ProductClass: compactProductLabelValueOptions(productOptionsContext.productClassOptions),
            ProductSubClass: compactProductSubClassOptions(productOptionsContext.productSubClassOptions),
            Manufacturer: compactProductLabelValueOptions(productOptionsContext.manufacturerOptions),
            ProductLine: compactProductLabelValueOptions(productOptionsContext.productLinesOptions),
            CountryOfOrigin: compactProductLabelValueOptions(productOptionsContext.countryOptions),
            ProductDefinition: {
                Physical: 1,
                Digital: 2,
            },
            Status: {
                Active: "1",
                Inactive: "2",
                Discontinued: "3",
            },
        };

        return {
            pageContext: {
                entityName: pageContext.entityName,
                pageName: pageContext.pageName,
            },
            requiredFields: requiredProductFields,
            fieldRequirements: {
                Name: 'Required. Customer-facing/internal item name shown in lists and documents.',
                ProductType: 'Required. Use the value from fieldOptions.ProductType.',
                Manufacturer: 'Required. Use the value from fieldOptions.Manufacturer.',
                ManufacturerProductId: 'Required. Manufacturer article/product number.',
                Status: 'Required. Use 1 for Active, 2 for Inactive, or 3 for Discontinued.',
                ProductClass: 'Required. Use the value from fieldOptions.ProductClass.',
                ValidFrom: 'Required. Date from which this product is valid/active.',
                ProductSubClass: 'Optional. If used, choose a subclass under the selected ProductClass.',
                TARIC: 'Optional in validation, but needed for export readiness. Do not invent if unknown.',
                CountryOfOrigin: 'Optional in validation, but needed for export readiness. Use fieldOptions.CountryOfOrigin.',
            },
            editableFields: productLlmEditableFields,
            readOnlyFields: productLlmReadOnlyFields,
            fieldOptions,
            compactDataShapes: {
                fieldOptions: '{ "Display label": value }',
                ProductSubClass: '{ "ProductClassValue": { "Subclass label": subclassValue } }',
                SupplierProducts: '{ "schema": ["SupplierProduct", "SupplierProduct_Value", "SupplierName", "ManufacturerProduct", "Price", "Status"], "rows": [...] }',
                SalesItems: '{ "schema": ["SalesItemId", "ArticleNumber", "SalesItemName", "Status", "MainItem"], "rows": [...] }',
            },
            currentValues: compactProductValues(formikProps.values),
            initialValues: compactProductValues(pageContext.initialValues),
            changedFields,
            relatedData: compactProductRelatedData(formikProps.values, pageContext.initialValues),
            pageState: {
                editId: pageContext.editId,
                currentTab: pageContext.currentTab,
            },
            additionalContext,
        };
    }, [additionalContext, changedFields, formikProps.values, pageContext, productOptionsContext]);

    const buildPrompt = useCallback(() => {
        return [
            productLlmPrompt,
            commonLlmJsonReturnPrompt,
            commonLlmOptionsPrompt,
            "Context data:",
            JSON.stringify(getPromptData(), null, 2),
        ].join("\n\n");
    }, [getPromptData]);

    const copyPromptToClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            await writeTextToClipboard(buildPrompt());
            setTemporaryButtonTitle(setCopyButtonTitle, "Copied", "Copy");
        } catch (error) {
            console.error("Failed to copy product LLM prompt.", error);
            setTemporaryButtonTitle(setCopyButtonTitle, "Copy failed", "Copy");
            window.alert("Could not copy the product prompt to the clipboard.");
        }
    }, [buildPrompt, setTemporaryButtonTitle]);

    const applyLlmResponseText = useCallback(async (responseText: string): Promise<ProductLlmApplyResult> => {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const nextValues = getFormikValuesFromLlmResponse(parsedResponse);
        const allowedValues = getAllowedFormikValues(nextValues, formikProps.values);

        if (Object.keys(allowedValues).length === 0) {
            return 'no-fields';
        }

        const currentValues = formikProps.values;
        const changedAllowedValues = getChangedFormikValues(allowedValues, currentValues);

        if (Object.keys(changedAllowedValues).length === 0) {
            return 'no-changes';
        }

        const nextChangedFields = getNextProductLlmChangedFields(changedFields, changedAllowedValues, currentValues);

        await formikProps.setValues({ ...currentValues, ...changedAllowedValues }, true);
        setChangedFields(nextChangedFields);

        return 'applied';
    }, [changedFields, formikProps]);

    const pasteValuesFromClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
                throw new Error("Clipboard read is not available in this browser.");
            }

            const clipboardText = await navigator.clipboard.readText();
            const applyResult = await applyLlmResponseText(clipboardText);

            if (applyResult === 'no-fields') {
                window.alert('No matching editable Product Formik fields were found in the pasted JSON. Expected either { "values": { ... } } or a JSON object with Product Formik field names.');
                return;
            }

            if (applyResult === 'no-changes') {
                setTemporaryButtonTitle(setPasteButtonTitle, "No changes", "Paste");
                return;
            }

            setTemporaryButtonTitle(setPasteButtonTitle, "Pasted", "Paste");
        } catch (error) {
            console.error("Failed to paste product LLM values.", error);
            setTemporaryButtonTitle(setPasteButtonTitle, "Paste failed", "Paste");
            window.alert("Could not paste product values. Make sure the clipboard contains valid JSON from the LLM.");
        }
    }, [applyLlmResponseText, setTemporaryButtonTitle]);

    const revertField = useCallback(async (fieldName: string) => {
        const change = changedFields[fieldName];

        if (!change) {
            return;
        }

        await formikProps.setFieldValue(fieldName, change.originalValue, true);

        setChangedFields((previousChangedFields) => {
            const nextChangedFields = { ...previousChangedFields };
            delete nextChangedFields[fieldName];
            return nextChangedFields;
        });
    }, [changedFields, formikProps]);

    return {
        copyPromptToClipboard,
        pasteValuesFromClipboard,
        applyLlmResponseText,
        buildPrompt,
        revertField,
        copyButtonTitle,
        pasteButtonTitle,
        changedFields,
    };
}

/**
 * Prototype local copy of the form container behavior.
 * Kept local so this feature can be tested without touching the shared FormContainerMolecule.
 */
function ProductLlmFormContainer({ formikProps, children }: { formikProps: FormikProps<any>; children: React.ReactNode }) {
    return (
        <Grid container spacing={2}>
            {children}
        </Grid>
    );
}

/**
 * Prototype local copy of FormField.
 * Kept local so the diff UI can be tested without changing the shared FormField.
 */
type ProductLlmResolvedType = 'date' | 'checkbox' | 'select' | 'multiline' | 'text';

interface ProductLlmFormFieldProps {
    fieldProps: any;
    formikProps?: any;
    children?: React.ReactNode;
    sx?: SxProps<Theme>;
}

function ProductLlmFormField({ fieldProps, formikProps, children, sx }: ProductLlmFormFieldProps) {
    const label = fieldProps.label ?? (fieldProps.name?.charAt(0).toUpperCase() + fieldProps.name?.slice(1));
    const onChangeFunction = fieldProps?.handleChange ?? formikProps?.handleChange;
    const productLlmClipboard = useProductLlmClipboardContext();

    const resolvedType: ProductLlmResolvedType =
        fieldProps.type ??
        (fieldProps.date
            ? 'date'
            : fieldProps.checkbox
                ? 'checkbox'
                : fieldProps.select
                    ? 'select'
                    : fieldProps.multiLine || fieldProps.multiline
                        ? 'multiline'
                        : 'text');

    const name = fieldProps.name;
    const touched = formikProps?.touched?.[name];
    const error = formikProps?.errors?.[name];
    const hasSubmitted = (formikProps?.submitCount ?? 0) > 0;
    const showError = (touched || hasSubmitted) && error;

    // full status object
    const status = formikProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText: string | undefined = fieldProps.tooltipText;

    // Hints from formik.status.hints
    const hints = status?.hints ?? {};
    const hintForField = hints?.[name];
    const showHint = !showError && Boolean(hintForField);

    // Field meta (warning, etc) from schema
    const fieldMeta = status?.fieldMeta?.[name] ?? {};
    const metaWarning: string | undefined = fieldMeta.warning;

    const value = formikProps?.values?.[name];
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    const showWarning =
        !showError &&
        !showHint &&
        Boolean(metaWarning) &&
        (touched || hasSubmitted) &&
        isEmpty;

    const baseHelperText = showError
        ? error
        : showHint
            ? hintForField
            : showWarning
                ? metaWarning
                : '';

    // If info-button mode is on and there is tooltipText, use it as info helper text
    const useTooltipInfo = !baseHelperText && !showError && showAllTooltips && !!tooltipText;

    const helperText = useTooltipInfo ? tooltipText : baseHelperText;

    const isError = Boolean(showError);
    const isInfo = !isError && (showHint || useTooltipInfo);
    const isWarning = !isError && !isInfo && showWarning;

    const diffOptions = getProductLlmDiffOptions(fieldProps, formikProps, productLlmClipboard);

    return (
        <>
            <Grid
                item
                display="flex"
                md={fieldProps.md ?? 12}
                xs={fieldProps.xs ?? 5.8}
            >
                <Grid
                    item
                    md={12}
                    xs={12}
                    display="flex"
                    alignItems="center"
                    width="100%"
                >
                    {resolvedType === 'date' ? (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label={label}
                                ampm={false}
                                disabled={fieldProps.disabled ?? false}
                                value={
                                    formikProps?.values?.[fieldProps.name] &&
                                        formikProps?.values?.[fieldProps.name] !== ''
                                        ? parseISO(formikProps?.values?.[fieldProps.name])
                                        : null
                                }
                                onChange={(value) => {
                                    formikProps.setFieldValue(
                                        fieldProps.name,
                                        value ? new Date(value).toISOString() : null
                                    );
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: 'normal',
                                        name: fieldProps.name,
                                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                            formikProps.setFieldTouched(fieldProps.name, true);
                                            formikProps.handleBlur(e);
                                        },
                                        error: Boolean(isError),
                                        helperText,
                                        sx,
                                        FormHelperTextProps: isInfo
                                            ? { sx: { color: 'info.main' } }
                                            : isWarning
                                                ? { sx: { color: 'warning.main' } }
                                                : undefined,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    ) : resolvedType === 'checkbox' ? (
                        <Box display="flex" alignItems="center">
                            <FormControlLabel
                                sx={sx}
                                control={
                                    <Checkbox
                                        name={fieldProps.name}
                                        checked={toBoolean(formikProps?.values?.[fieldProps.name]) ?? false}
                                        value={formikProps?.values?.[fieldProps.name] ?? false}
                                        onBlur={formikProps?.handleBlur}
                                        onChange={onChangeFunction}
                                    />
                                }
                                label={label}
                            />
                            <Stack display="flex" spacing={2}>
                                {Boolean(isError) && (
                                    <FormHelperText error>{helperText}</FormHelperText>
                                )}
                                {!isError && isInfo && (
                                    <FormHelperText sx={{ color: 'info.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                                {!isError && !isInfo && isWarning && (
                                    <FormHelperText sx={{ color: 'warning.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Box>
                    ) : (
                        <ProductLlmCustomField
                            options={{
                                ...fieldProps,
                                ...diffOptions,
                                tooltipText: fieldProps.tooltipText,
                                select: fieldProps.select ?? (resolvedType === 'select'),
                                multiLine: fieldProps.multiLine ?? fieldProps.multiline ?? (resolvedType === 'multiline'),
                            }}
                            stateProps={formikProps}
                            sx={fieldProps.sx ?? sx}
                        >
                            {children}
                        </ProductLlmCustomField>
                    )}

                    <TooltipField tooltipText={fieldProps.tooltipText} />
                </Grid>
            </Grid>
        </>
    );
}

/**
 * Prototype local copy of CustomField.
 * This is where the diff display is added directly to TextField.
 */
interface ProductLlmCustomFieldProps {
    stateProps?: {
        values?: any;
        handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
        touched?: any;
        errors?: any;
        handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
        state?: any;
        setState?: React.Dispatch<React.SetStateAction<any>>;
        submitCount?: number;
        status?: any;
    };
    options: {
        label?: string;
        disabled?: boolean;
        multiLine?: boolean;
        multiline?: boolean;
        select?: boolean;
        isMulti?: boolean;
        type?: string;
        name: string;
        height?: string;
        topMargin?: string;
        selectOpen?: boolean;
        className?: string;
        setSelectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
        rows?: number;
        maxRows?: number;
        placeholder?: string;
        warning?: string | ((ctx: { value: any; values: any }) => string | undefined);
        tooltipText?: string;
        llmDiffActive?: boolean;
        llmDiffOriginalValue?: any;
        llmDiffFieldName?: string;
        llmDiffOnRevert?: (fieldName: string) => Promise<void>;
    };
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

function ProductLlmCustomField({ options, stateProps, children, sx }: ProductLlmCustomFieldProps) {
    const { t } = useTranslation();

    const label = t(
        options.label ??
        options.name.charAt(0).toUpperCase() + options.name.slice(1)
    );

    const onChangeFunction =
        stateProps?.setState != undefined
            ? (event: React.ChangeEvent<HTMLInputElement>) => {
                stateProps?.setState(event.target.value);
            }
            : stateProps?.handleChange;

    let value = stateProps?.state ?? get(stateProps?.values ?? {}, options.name, '');

    if (
        options.select &&
        !options.isMulti &&
        (typeof value === 'object' || value === undefined)
    ) {
        value = '';
    }

    const [selectOpen, setSelectOpen] = useState(false);

    useEffect(() => {
        if (options.selectOpen !== undefined) {
            setSelectOpen(options.selectOpen);
        }
    }, [options.selectOpen]);

    const handleSelectOpen = () => {
        setSelectOpen(true);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(true);
        }
    };

    const handleSelectClose = () => {
        setSelectOpen(false);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(false);
        }
    };

    const isTouched = get(stateProps?.touched, options.name);
    const error = get(stateProps?.errors, options.name);
    const hasSubmitted = (stateProps?.submitCount ?? 0) > 0;
    const showError = (isTouched || hasSubmitted) && error;

    // --- generic warning logic ---
    const values = stateProps?.values ?? {};
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    let warningText = '';

    if (!showError && options.warning && (isTouched || hasSubmitted)) {
        if (typeof options.warning === 'string') {
            // only warn when empty
            if (isEmpty) {
                warningText = options.warning;
            }
        } else if (typeof options.warning === 'function') {
            warningText = options.warning({ value, values }) ?? '';
        }
    }

    // --- info from tooltip when FormInfoButton is active ---
    const status = stateProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText = options.tooltipText;

    const showTooltipInfo = showAllTooltips && !!tooltipText && !showError && !warningText;

    const helperText = showError ? error : warningText || (showTooltipInfo ? tooltipText : '');

    const isError = Boolean(showError);
    const isWarning = !isError && Boolean(warningText);
    const isInfo = !isError && !isWarning && showTooltipInfo;

    const diffActive = Boolean(options.llmDiffActive);
    const diffOriginalValue = formatProductLlmPreviewValue(options.llmDiffOriginalValue);

    const baseSx: SxProps<Theme> = {
        '&.MuiFormControl-root': {
            marginTop: options?.topMargin ?? '16px',
        },
        '& .MuiOutlinedInput-root': {
            height:
                options?.height == null ? 'auto' : options.height + '  !important',
        },
        '& label.MuiInputLabel-root': {
            marginTop: options?.height == null ? '0' : '-5px  !important',
        },
        ...(isWarning && {
            '& .MuiFormHelperText-root': {
                color: 'warning.main',
            },
        }),
        ...(isInfo && {
            '& .MuiFormHelperText-root': {
                color: 'info.main',
            },
        }),
    };

    const diffSx = diffActive
    ? {
        '& .MuiOutlinedInput-root': {
            position: 'relative',
            '& fieldset': {
                borderColor: '#86bde8',
            },
            '&:hover fieldset': {
                borderColor: '#86bde8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#86bde8',
            },
            '& .product-llm-revert-button': {
                opacity: 0,
                transition: 'opacity 120ms ease-in-out',
            },
            '&:hover .product-llm-revert-button': {
                opacity: 1,
            },
        },
        '& .MuiInputBase-input': {
            paddingTop: options.multiLine ? '22px !important' : '22px !important',
            paddingBottom: options.multiLine ? '8px !important' : '7px !important',
        },
        '& .MuiSelect-select': {
            paddingTop: '22px !important',
            paddingBottom: '7px !important',
        },
        '& textarea.MuiInputBase-input': {
            paddingTop: '22px !important',
        },
    }
    : {};

    return (
        <TextField
            label={label}
            name={options.name}
            value={value}
            className={
                options.className ??
                'custom-field-' + label?.toLowerCase().replace(' ', '-')
            }
            onBlur={stateProps?.handleBlur}
            multiline={options.multiLine ?? false}
            rows={options.rows}
            maxRows={options.maxRows}
            placeholder={options.placeholder}
            onChange={onChangeFunction}
            inputProps={{ readOnly: options.disabled }}
            select={options.select ?? false}
            SelectProps={{
                multiple: options.isMulti ?? false,
                open: selectOpen,
                onOpen: handleSelectOpen,
                onClose: handleSelectClose,
            }}
            InputLabelProps={diffActive ? { shrink: true } : undefined}
            InputProps={diffActive ? {
                startAdornment: (
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: 6,
                            left: 14,
                            right: options.select ? 70 : 46,
                            zIndex: 2,
                            //color: '#86bde8',
                            fontStyle: 'italic',
                            fontSize: '0.68rem',
                            opacity: 0.8,
                            fontWeight: 500,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            //textDecoration: 'line-through',
                            pointerEvents: 'none',
                        }}
                    >
                        {diffOriginalValue}
                    </Box>
                ),
                endAdornment: (
                    <IconButton
                        className="product-llm-revert-button"
                        size="small"
                        title="Revert to previous value"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (options.llmDiffFieldName && options.llmDiffOnRevert) {
                                options.llmDiffOnRevert(options.llmDiffFieldName);
                            }
                        }}
                        sx={{
                            mr: options.select ? 2.25 : 0,
                            color: '#86bde8',
                        }}
                    >
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                ),
            } : undefined}
            helperText={helperText}
            error={isError}
            type={options.type ?? 'text'}
            variant="outlined"
            fullWidth
            sx={[
                baseSx,
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                diffSx,
            ]}
            margin="normal"
        >
            {children}
        </TextField>
    );
}


/**
 * Product LLM API popup/config helpers.
 * Keep future changes to the API behavior mostly in this block.
 */
type ProductLlmAiOutputFormat = "auto" | "json_object" | "text";
type ProductLlmAiAssistantPrefillMode = "auto" | "always" | "never";

type ProductLlmAiConfig = {
    url: string;
    apiKey: string;
    model: string;
    temperature: string;
    outputFormat: ProductLlmAiOutputFormat;
    assistantPrefillMode: ProductLlmAiAssistantPrefillMode;
    assistantPrefill: string;
    promptCacheKey: string;
    promptCacheRetention: string;
};

type ProductLlmAiAttachmentKind = "pdf" | "image" | "text" | "unsupported";

type ProductLlmAiAttachment = {
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
    kind: ProductLlmAiAttachmentKind;
    text?: string;
    dataUrl?: string;
    truncated?: boolean;
    omittedReason?: string;
};

const productLlmAiTextAttachmentMaxChars = 30000;
const productLlmAiImageAttachmentMaxBytes = 8 * 1024 * 1024;
const productLlmAiPdfAttachmentMaxBytes = 50 * 1024 * 1024;
const productLlmAiAttachmentAccept = [
    "application/pdf",
    ".pdf",
    "image/*",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/json",
    "application/xml",
    ".txt",
    ".md",
    ".markdown",
    ".csv",
    ".tsv",
    ".json",
    ".jsonl",
    ".xml",
    ".html",
    ".htm",
    ".yaml",
    ".yml",
    ".js",
    ".jsx",
    ".ts",
    ".tsx",
    ".css",
    ".scss",
    ".sql",
].join(",");

const productLlmAiTextAttachmentMimeTypes = [
    "application/json",
    "application/xml",
    "application/x-yaml",
    "text/",
];

const productLlmAiTextAttachmentExtensions = [
    ".txt",
    ".md",
    ".markdown",
    ".csv",
    ".tsv",
    ".json",
    ".jsonl",
    ".xml",
    ".html",
    ".htm",
    ".yaml",
    ".yml",
    ".js",
    ".jsx",
    ".ts",
    ".tsx",
    ".css",
    ".scss",
    ".sql",
];

const productLlmAiConfigStorageKey = "productLlmAiConfig";
const productLlmAiConfigChangedEvent = "productLlmAiConfigChanged";
const productLlmAiPartialApplyIntervalMs = 500;

const productLlmAiModelOptions = [
    "gpt-5.6-sol",
    "gpt-5.6-terra",
    "gpt-5.6-luna",
    "gpt-5.5",
    "gpt-5.4-mini",
    "gpt-5.4-nano",
];

const productLlmAiModelOptionMeta: Record<string, { group: string; description: string }> = {
    "gpt-5.6-sol": { group: "GPT-5.6", description: "Flagship model for complex professional work." },
    "gpt-5.6-terra": { group: "GPT-5.6", description: "Balanced GPT-5.6 model for intelligence and cost." },
    "gpt-5.6-luna": { group: "GPT-5.6", description: "Fastest, most affordable GPT-5.6 model." },
    "gpt-5.5": { group: "Frontier", description: "Best current frontier model for complex work." },
    "gpt-5.4-mini": { group: "Cost-efficient", description: "Balanced cheap model for coding, reasoning, vision, and tool use." },
    "gpt-5.4-nano": { group: "Cost-efficient", description: "Lowest-latency, high-volume GPT-5.4 option." },
};

const productLlmAiDefaultConfig: ProductLlmAiConfig = {
    url: "https://api.openai.com/v1/chat/completions",
    apiKey: "",
    model: "gpt-5.4-mini-2026-03-17",
    temperature: "",
    outputFormat: "auto",
    assistantPrefillMode: "auto",
    assistantPrefill: '{"values":{"',
    promptCacheKey: "dacsa-product-form-v1",
    promptCacheRetention: "",
};

const productLlmAiBaseSystemPrompt = "You update Formik form values. Return only valid JSON. Prefer the { values: { ... } } shape. Do not return markdown unless it contains only one JSON codeblock.";

function getProductLlmAiSystemPrompt(assistantPrefill: string, outputFormat: ProductLlmAiOutputFormat) {
    const jsonModeInstruction = outputFormat === "json_object" ? " The API enforces JSON object output; return one complete JSON object." : "";

    return normalizeProductLlmAiAssistantPrefill(assistantPrefill)
        ? `${productLlmAiBaseSystemPrompt}${jsonModeInstruction} Continue the assistant message from the provided JSON prefix. Do not repeat the prefix. Do not add prose. Close any opened JSON objects.`
        : `${productLlmAiBaseSystemPrompt}${jsonModeInstruction}`;
}

function normalizeProductLlmAiAssistantPrefill(assistantPrefill?: string | null) {
    return (assistantPrefill ?? "").trim();
}

function applyProductLlmAssistantPrefill(content: string, assistantPrefill?: string | null) {
    const normalizedPrefill = normalizeProductLlmAiAssistantPrefill(assistantPrefill);
    const trimmedContent = (content ?? "").trim();

    if (!normalizedPrefill || !trimmedContent) {
        return trimmedContent || normalizedPrefill;
    }

    if (trimmedContent.startsWith(normalizedPrefill) || trimmedContent.startsWith("{")) {
        return trimmedContent;
    }

    return normalizedPrefill + trimmedContent;
}

function ProductLlmAiConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [config, setConfig] = useProductLlmAiConfig();
    const [draftConfig, setDraftConfig] = useState<ProductLlmAiConfig>(config);

    useEffect(() => {
        if (open) {
            setDraftConfig(config);
        }
    }, [config, open]);

    const handleChange = (fieldName: keyof ProductLlmAiConfig) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraftConfig((previousConfig) => ({ ...previousConfig, [fieldName]: event.target.value }));
    };

    const handleSave = () => {
        setConfig({
            url: draftConfig.url.trim() || productLlmAiDefaultConfig.url,
            apiKey: draftConfig.apiKey.trim(),
            model: draftConfig.model.trim() || productLlmAiDefaultConfig.model,
            temperature: draftConfig.temperature.trim(),
            outputFormat: draftConfig.outputFormat,
            assistantPrefillMode: draftConfig.assistantPrefillMode,
            assistantPrefill: draftConfig.assistantPrefill.trim(),
            promptCacheKey: draftConfig.promptCacheKey.trim(),
            promptCacheRetention: draftConfig.promptCacheRetention.trim(),
        });
        onClose();
    };

    const handleReset = () => {
        setDraftConfig(productLlmAiDefaultConfig);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>AI configuration</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="API URL"
                        value={draftConfig.url}
                        onChange={handleChange("url")}
                        fullWidth
                        helperText="Include protocol and port if needed, for example http://localhost:11434/v1/chat/completions."
                    />
                    <TextField
                        label="API key"
                        value={draftConfig.apiKey}
                        onChange={handleChange("apiKey")}
                        fullWidth
                        type="password"
                        autoComplete="off"
                        helperText="Stored in localStorage. Leave empty for local endpoints that do not require a key."
                    />
                    <Autocomplete
                        freeSolo
                        autoHighlight
                        options={productLlmAiModelOptions}
                        value={draftConfig.model}
                        inputValue={draftConfig.model}
                        groupBy={(option) => productLlmAiModelOptionMeta[option]?.group ?? "Custom"}
                        onInputChange={(_, nextInputValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextInputValue }));
                        }}
                        onChange={(_, nextValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextValue ?? "" }));
                        }}
                        renderOption={(props, option) => {
                            const meta = productLlmAiModelOptionMeta[option];

                            return (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Box component="span" fontWeight={600}>{option}</Box>
                                        {meta?.description ? (
                                            <Box component="span" color="text.secondary" fontSize="0.8rem" ml={1}>
                                                {meta.description}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model"
                                fullWidth
                                helperText="Choose a common OpenAI model or type any OpenAI-compatible model name."
                            />
                        )}
                    />
                    <TextField
                        label="Temperature"
                        value={draftConfig.temperature}
                        onChange={handleChange("temperature")}
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, max: 2, step: 0.1 }}
                        placeholder="Leave empty to omit"
                        helperText="Leave empty to use the model/API default. Set 1 only for endpoints or models that require it."
                    />
                    <TextField
                        label="Output format"
                        value={draftConfig.outputFormat}
                        onChange={handleChange("outputFormat")}
                        select
                        fullWidth
                        helperText="Auto uses JSON object mode for api.openai.com and plain text for local OpenAI-compatible endpoints."
                    >
                        <MenuItem value="auto">Auto</MenuItem>
                        <MenuItem value="json_object">JSON object</MenuItem>
                        <MenuItem value="text">Text / prompt only</MenuItem>
                    </TextField>
                    <TextField
                        label="Assistant prefill mode"
                        value={draftConfig.assistantPrefillMode}
                        onChange={handleChange("assistantPrefillMode")}
                        select
                        fullWidth
                        helperText="Auto disables prefill for api.openai.com and enables it for local OpenAI-compatible endpoints."
                    >
                        <MenuItem value="auto">Auto</MenuItem>
                        <MenuItem value="always">Always send prefill</MenuItem>
                        <MenuItem value="never">Never send prefill</MenuItem>
                    </TextField>
                    <TextField
                        label="Assistant prefill"
                        value={draftConfig.assistantPrefill}
                        onChange={handleChange("assistantPrefill")}
                        fullWidth
                        placeholder='Example: { or {"values":{'
                        helperText='For llama.cpp/vLLM continuation only. Auto mode does not send this to api.openai.com because assistant-message continuation is not a reliable JSON-format control.'
                    />
                    <TextField
                        label="Prompt cache key"
                        value={draftConfig.promptCacheKey}
                        onChange={handleChange("promptCacheKey")}
                        fullWidth
                        helperText="Sent to OpenAI-compatible OpenAI URLs as prompt_cache_key. Keep stable per page/entity version, for example dacsa-product-form-v1."
                    />
                    <TextField
                        label="Prompt cache retention"
                        value={draftConfig.promptCacheRetention}
                        onChange={handleChange("promptCacheRetention")}
                        fullWidth
                        placeholder="Optional, for example 24h"
                        helperText="Optional OpenAI prompt_cache_retention. Leave empty to use the API default. Not sent to local llama.cpp URLs."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset}>Reset defaults</Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function ProductLlmAiRunDialog({
    open,
    onClose,
    buildPrompt,
    applyLlmResponseText,
}: {
    open: boolean;
    onClose: () => void;
    buildPrompt: () => string;
    applyLlmResponseText: (responseText: string) => Promise<ProductLlmApplyResult>;
}) {
    const [config] = useProductLlmAiConfig();
    const [userPrompt, setUserPrompt] = useState("");
    const [attachments, setAttachments] = useState<ProductLlmAiAttachment[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    const [attachmentErrorMessage, setAttachmentErrorMessage] = useState("");
    const [debugExpanded, setDebugExpanded] = useState(false);
    const [debugResponseText, setDebugResponseText] = useState("");
    const [debugParsedSummary, setDebugParsedSummary] = useState("");
    const openRef = useRef(open);
    const partialApplyTimerRef = useRef<number | null>(null);
    const latestPartialJsonRef = useRef("");
    const lastAppliedPartialJsonRef = useRef("");
    const partialApplyPromiseRef = useRef<Promise<void>>(Promise.resolve());
    const hasAppliedStreamedValuesRef = useRef(false);

    useEffect(() => {
        openRef.current = open;
    }, [open]);

    useEffect(() => {
        return () => {
            if (partialApplyTimerRef.current !== null) {
                window.clearTimeout(partialApplyTimerRef.current);
            }
        };
    }, []);

    const canRun = (Boolean(userPrompt.trim()) || attachments.some((attachment) => attachment.kind !== "unsupported")) && Boolean(config.url.trim()) && !isRunning;

    const runPartialApply = useCallback((partialJsonText: string) => {
        partialApplyPromiseRef.current = partialApplyPromiseRef.current
            .catch(() => undefined)
            .then(async () => {
                if (!partialJsonText || partialJsonText === lastAppliedPartialJsonRef.current) {
                    return;
                }

                lastAppliedPartialJsonRef.current = partialJsonText;
                const applyResult = await applyLlmResponseText(partialJsonText);

                if (applyResult === 'applied') {
                    hasAppliedStreamedValuesRef.current = true;

                    if (openRef.current) {
                        setStatusMessage("Applying completed AI fields while the response streams...");
                    }
                }
            });
    }, [applyLlmResponseText]);

    const schedulePartialApply = useCallback((partialJsonText: string) => {
        latestPartialJsonRef.current = partialJsonText;

        if (partialApplyTimerRef.current !== null) {
            return;
        }

        partialApplyTimerRef.current = window.setTimeout(() => {
            partialApplyTimerRef.current = null;
            runPartialApply(latestPartialJsonRef.current);
        }, productLlmAiPartialApplyIntervalMs);
    }, [runPartialApply]);

    const flushPartialApply = useCallback(async () => {
        if (partialApplyTimerRef.current !== null) {
            window.clearTimeout(partialApplyTimerRef.current);
            partialApplyTimerRef.current = null;
            runPartialApply(latestPartialJsonRef.current);
        }

        await partialApplyPromiseRef.current;
    }, [runPartialApply]);

    const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []) as File[];
        event.target.value = "";

        if (files.length === 0) {
            return;
        }

        setAttachmentErrorMessage("");

        try {
            const nextAttachments = await readProductLlmAiAttachmentsFromFiles(files);
            setAttachments((previousAttachments) => [...previousAttachments, ...nextAttachments]);
        } catch (error) {
            console.error("Failed to read product LLM attachments.", error);
            setAttachmentErrorMessage(error instanceof Error ? error.message : "Could not read one or more attachments.");
        }
    };

    const handleRemoveAttachment = (attachmentId: string) => {
        setAttachments((previousAttachments) => previousAttachments.filter((attachment) => attachment.id !== attachmentId));
    };

    const handleRun = async () => {
        if (!canRun) {
            return;
        }

        setIsRunning(true);
        setErrorMessage("");
        setStatusMessage("");
        setDebugResponseText("");
        setDebugParsedSummary("");
        latestPartialJsonRef.current = "";
        lastAppliedPartialJsonRef.current = "";
        partialApplyPromiseRef.current = Promise.resolve();
        hasAppliedStreamedValuesRef.current = false;

        try {
            const formPrompt = buildPrompt();
            const requestArgs = {
                config,
                userPrompt,
                formPrompt,
                attachments,
                onPartialJsonText: (partialJsonText: string) => {
                    if (!partialJsonText || partialJsonText === latestPartialJsonRef.current) {
                        return;
                    }

                    schedulePartialApply(partialJsonText);
                },
            };
            const initialResponseText = await requestProductLlmAiResponse(requestArgs);
            let responseText = initialResponseText;
            let debugResponseText = initialResponseText;

            await flushPartialApply();

            if (!hasProductLlmEditableFieldsInResponse(initialResponseText)) {
                setStatusMessage("The first AI response was empty. Retrying once with a stricter non-empty output instruction...");
                responseText = await requestProductLlmAiResponse({
                    ...requestArgs,
                    userPrompt: buildProductLlmAiNonEmptyRetryPrompt(userPrompt, initialResponseText),
                    onPartialJsonText: requestArgs.onPartialJsonText,
                });
                await flushPartialApply();
                debugResponseText = [
                    "--- First response ---",
                    initialResponseText,
                    "",
                    "--- Corrective retry response ---",
                    responseText,
                ].join("\n");
            }

            setDebugResponseText(debugResponseText);
            setDebugParsedSummary(getProductLlmAiResponseDebugSummary(responseText));

            const applyResult = await applyLlmResponseText(responseText);
            const hasAppliedStreamedValues = hasAppliedStreamedValuesRef.current;

            if (applyResult === 'no-fields' && !hasAppliedStreamedValues) {
                if (openRef.current) {
                    setDebugExpanded(true);
                    setErrorMessage(`The AI response contained JSON, but no matching editable Formik fields were found. ${getProductLlmAiResponseKeySummary(responseText)}`);
                }
                return;
            }

            if (applyResult === 'no-changes' && !hasAppliedStreamedValues) {
                if (openRef.current) {
                    setStatusMessage("AI response was valid, but it did not change any current field values.");
                }
                onClose();
                return;
            }

            if (openRef.current) {
                setStatusMessage(hasAppliedStreamedValues ? "AI response streamed and applied to the form." : "AI response applied to the form.");
            }
        } catch (error) {
            console.error("Failed to run product LLM request.", error);

            if (openRef.current) {
                setDebugExpanded(true);
                setErrorMessage(error instanceof Error ? error.message : "Could not run the AI request.");
            }
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Run AI update</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="Prompt"
                        value={userPrompt}
                        onChange={(event) => setUserPrompt(event.target.value)}
                        fullWidth
                        multiline
                        rows={7}
                        disabled={isRunning}
                        placeholder="Example: Fill in missing product fields from this text..."
                    />
                    <Box>
                        <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} disabled={isRunning}>
                            Attach files/images
                            <input hidden type="file" multiple accept={productLlmAiAttachmentAccept} onChange={handleAttachmentChange} />
                        </Button>
                        <FormHelperText>
                            PDFs and images are sent as multimodal/file attachments when the configured endpoint supports them. Text files are inserted into the prompt.
                        </FormHelperText>
                        {attachmentErrorMessage ? <Alert severity="error" sx={{ mt: 1 }}>{attachmentErrorMessage}</Alert> : null}
                        {attachments.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                                {attachments.map((attachment) => (
                                    <Chip
                                        key={attachment.id}
                                        label={getProductLlmAiAttachmentChipLabel(attachment)}
                                        onDelete={isRunning ? undefined : () => handleRemoveAttachment(attachment.id)}
                                        color={attachment.kind === "unsupported" ? "warning" : "default"}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        ) : null}
                    </Box>
                    {isRunning ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} />
                            <span>Calling AI and applying completed JSON fields as they stream...</span>
                        </Box>
                    ) : null}
                    {statusMessage ? <Alert severity="success">{statusMessage}</Alert> : null}
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                    <Box>
                        <Button variant="text" onClick={() => setDebugExpanded((expanded) => !expanded)} disabled={!debugResponseText && !debugParsedSummary}>
                            {debugExpanded ? "Hide AI response" : "Show AI response"}
                        </Button>
                        <Collapse in={debugExpanded}>
                            <Stack spacing={1.5} mt={1}>
                                <TextField label="Parsed response summary" value={debugParsedSummary || "No response has been received yet."} fullWidth multiline minRows={3} maxRows={8} InputProps={{ readOnly: true }} />
                                <TextField label="Response text passed to the form parser" value={debugResponseText || "No response has been received yet."} fullWidth multiline minRows={8} maxRows={24} InputProps={{ readOnly: true }} />
                                <Button variant="outlined" onClick={() => writeTextToClipboard(debugResponseText)} disabled={!debugResponseText}>Copy response</Button>
                            </Stack>
                        </Collapse>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={handleRun} variant="contained" disabled={!canRun}>Run</Button>
            </DialogActions>
        </Dialog>
    );
}

function useProductLlmAiConfig(): [ProductLlmAiConfig, (nextConfig: ProductLlmAiConfig) => void] {
    const [config, setConfigState] = useState<ProductLlmAiConfig>(() => getStoredProductLlmAiConfig());

    const setConfig = useCallback((nextConfig: ProductLlmAiConfig) => {
        setConfigState(nextConfig);
        saveStoredProductLlmAiConfig(nextConfig);
    }, []);

    useEffect(() => {
        const refreshConfig = () => setConfigState(getStoredProductLlmAiConfig());
        const handleStorage = (event: StorageEvent) => {
            if (event.key === productLlmAiConfigStorageKey) {
                refreshConfig();
            }
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener(productLlmAiConfigChangedEvent, refreshConfig);

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener(productLlmAiConfigChangedEvent, refreshConfig);
        };
    }, []);

    return [config, setConfig];
}

function getStoredProductLlmAiConfig(): ProductLlmAiConfig {
    if (typeof window === "undefined") {
        return productLlmAiDefaultConfig;
    }

    try {
        const rawConfig = window.localStorage.getItem(productLlmAiConfigStorageKey);
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : {};

        return {
            ...productLlmAiDefaultConfig,
            ...parsedConfig,
            url: parsedConfig?.url || productLlmAiDefaultConfig.url,
            apiKey: parsedConfig?.apiKey || "",
            model: parsedConfig?.model || productLlmAiDefaultConfig.model,
            temperature: parsedConfig?.temperature ?? productLlmAiDefaultConfig.temperature,
            outputFormat: parsedConfig?.outputFormat ?? productLlmAiDefaultConfig.outputFormat,
            assistantPrefillMode: parsedConfig?.assistantPrefillMode ?? productLlmAiDefaultConfig.assistantPrefillMode,
            assistantPrefill: parsedConfig?.assistantPrefill ?? productLlmAiDefaultConfig.assistantPrefill,
            promptCacheKey: parsedConfig?.promptCacheKey ?? productLlmAiDefaultConfig.promptCacheKey,
            promptCacheRetention: parsedConfig?.promptCacheRetention ?? productLlmAiDefaultConfig.promptCacheRetention,
        };
    } catch (error) {
        console.error("Failed to load product LLM AI config from localStorage.", error);
        return productLlmAiDefaultConfig;
    }
}

function saveStoredProductLlmAiConfig(config: ProductLlmAiConfig) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(productLlmAiConfigStorageKey, JSON.stringify(config));
    window.dispatchEvent(new Event(productLlmAiConfigChangedEvent));
}

type ProductLlmAiPartialJsonHandler = (partialJsonText: string) => Promise<void> | void;

type ProductLlmAiRequestArgs = {
    config: ProductLlmAiConfig;
    userPrompt: string;
    formPrompt: string;
    attachments?: ProductLlmAiAttachment[];
    onPartialJsonText?: ProductLlmAiPartialJsonHandler;
};

type ProductLlmAiMessageContentPart =
    | { type: "text"; text: string }
    | { type: "file"; file: { filename: string; file_data: string } }
    | { type: "image_url"; image_url: { url: string; detail: "auto" } };

type ProductLlmAiMessageContent = string | ProductLlmAiMessageContentPart[];

type ProductLlmAiChatMessage = {
    role: "system" | "user" | "assistant";
    content: ProductLlmAiMessageContent;
};

type ProductLlmAiWorkerMessage =
    | { type: "partial"; partialJsonText: string }
    | { type: "done"; jsonText: string }
    | { type: "error"; message: string };

let productLlmAiWorkerUrl: string | null = null;

async function requestProductLlmAiResponse(args: ProductLlmAiRequestArgs) {
    if (canUseProductLlmAiWorker()) {
        return requestProductLlmAiResponseInWorker(args);
    }

    return requestProductLlmAiResponseOnMainThread(args);
}

function canUseProductLlmAiWorker() {
    return typeof window !== "undefined" && typeof Worker !== "undefined" && typeof Blob !== "undefined" && typeof URL !== "undefined";
}

function requestProductLlmAiResponseInWorker({
    config,
    userPrompt,
    formPrompt,
    attachments = [],
    onPartialJsonText,
}: ProductLlmAiRequestArgs) {
    return new Promise<string>((resolve, reject) => {
        let settled = false;
        const worker = new Worker(getProductLlmAiWorkerUrl());

        const cleanup = () => {
            worker.onmessage = null;
            worker.onerror = null;
            worker.terminate();
        };

        worker.onmessage = (event: MessageEvent<ProductLlmAiWorkerMessage>) => {
            const message = event.data;

            if (!message) {
                return;
            }

            if (message.type === "partial") {
                onPartialJsonText?.(message.partialJsonText);
                return;
            }

            if (settled) {
                return;
            }

            settled = true;
            cleanup();

            if (message.type === "done") {
                resolve(message.jsonText);
                return;
            }

            reject(new Error(message.message || "The AI worker failed."));
        };

        worker.onerror = (event) => {
            if (settled) {
                return;
            }

            settled = true;
            event.preventDefault();
            cleanup();
            reject(new Error(event.message || "The AI worker failed."));
        };

        worker.postMessage({
            type: "run",
            payload: {
                config,
                defaultModel: productLlmAiDefaultConfig.model,
                systemPrompt: getProductLlmAiSystemPrompt(resolveProductLlmAiAssistantPrefill(config), resolveProductLlmAiOutputFormat(config)),
                assistantPrefill: resolveProductLlmAiAssistantPrefill(config),
                promptText: buildProductLlmAiPrompt(userPrompt, formPrompt, attachments),
                attachments,
            },
        });
    });
}

function getProductLlmAiWorkerUrl() {
    if (productLlmAiWorkerUrl) {
        return productLlmAiWorkerUrl;
    }

    const workerSource = String.raw`
self.onmessage = function (event) {
    var message = event.data;

    if (!message || message.type !== "run") {
        return;
    }

    runProductLlmAiWorker(message.payload).catch(function (error) {
        self.postMessage({ type: "error", message: error instanceof Error ? error.message : "Could not run the AI request." });
    });
};

var pendingPartialJsonText = "";
var lastPostedPartialJsonText = "";
var partialPostTimer = null;

function isProductLlmOpenAiApiUrl(url) {
    return /(^https?:\/\/)?api\.openai\.com\//i.test(String(url || ""));
}

function resolveProductLlmAiOutputFormat(config) {
    if (config.outputFormat === "json_object" || config.outputFormat === "text") {
        return config.outputFormat;
    }

    return isProductLlmOpenAiApiUrl(config.url) ? "json_object" : "text";
}

function getProductLlmAiTemperature(value) {
    var normalizedValue = String(value == null ? "" : value).trim();

    if (!normalizedValue) {
        return null;
    }

    var temperature = Number(normalizedValue);

    if (!Number.isFinite(temperature) || temperature < 0 || temperature > 2) {
        throw new Error("Temperature must be empty or a number from 0 to 2.");
    }

    return temperature;
}

async function runProductLlmAiWorker(payload) {
    pendingPartialJsonText = "";
    lastPostedPartialJsonText = "";

    if (partialPostTimer !== null) {
        clearTimeout(partialPostTimer);
        partialPostTimer = null;
    }

    var config = payload.config || {};
    var assistantPrefill = normalizeProductLlmAiAssistantPrefill(payload.assistantPrefill || config.assistantPrefill);
    var messages = buildProductLlmAiMessages({
        systemPrompt: payload.systemPrompt || "You update Formik form values. Return only valid JSON. Prefer the { values: { ... } } shape. Do not return markdown unless it contains only one JSON codeblock.",
        promptText: payload.promptText,
        attachments: payload.attachments || [],
        assistantPrefill: assistantPrefill
    });

    var requestBody = {
        model: config.model || payload.defaultModel,
        messages: messages,
        stream: true
    };
    var temperature = getProductLlmAiTemperature(config.temperature);
    var outputFormat = resolveProductLlmAiOutputFormat(config);

    if (temperature !== null) {
        requestBody.temperature = temperature;
    }

    if (outputFormat === "json_object") {
        requestBody.response_format = { type: "json_object" };
    }

    if (isProductLlmOpenAiApiUrl(config.url)) {
        if (config.promptCacheKey) {
            requestBody.prompt_cache_key = String(config.promptCacheKey).trim();
        }

        if (config.promptCacheRetention) {
            requestBody.prompt_cache_retention = String(config.promptCacheRetention).trim();
        }
    } else {
        requestBody.cache_prompt = true;
    }

    var response = await fetch(config.url, {
        method: "POST",
        headers: Object.assign(
            { "Content-Type": "application/json" },
            config.apiKey ? { Authorization: "Bearer " + config.apiKey } : {}
        ),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        var responseBodyText = await response.text();
        throw new Error(getProductLlmApiErrorMessage(response.status, responseBodyText));
    }

    if (!response.body) {
        var nonStreamResponseBodyText = await response.text();
        self.postMessage({ type: "done", jsonText: getProductLlmJsonTextFromResponseText(nonStreamResponseBodyText, assistantPrefill) });
        return;
    }

    var reader = response.body.getReader();
    var decoder = new TextDecoder();
    var rawResponseText = "";
    var content = "";
    var sseBuffer = "";

    while (true) {
        var readResult = await reader.read();
        var chunkText = decoder.decode(readResult.value || new Uint8Array(), { stream: !readResult.done });

        if (chunkText) {
            rawResponseText += chunkText;
            sseBuffer += chunkText;

            var lines = sseBuffer.split(/\r?\n/);
            sseBuffer = lines.pop() || "";

            for (var i = 0; i < lines.length; i++) {
                var line = lines[i];
                var trimmedLine = line.trim();

                if (!trimmedLine || !trimmedLine.startsWith("data:")) {
                    continue;
                }

                var data = trimmedLine.slice(5).trim();

                if (!data || data === "[DONE]") {
                    continue;
                }

                var parsedData = safeJsonParse(data);
                var deltaText = getProductLlmAiStreamDeltaContent(parsedData);

                if (deltaText) {
                    content += deltaText;
                    scheduleCompletedPartialProductLlmJsonText(applyProductLlmAssistantPrefill(content, assistantPrefill), false);
                }
            }

            if (!rawResponseText.includes("data:")) {
                var nonSseBody = safeJsonParse(rawResponseText);
                var nonSseContent = getProductLlmAiResponseContent(nonSseBody) || rawResponseText;

                if (nonSseContent) {
                    content = nonSseContent;
                    scheduleCompletedPartialProductLlmJsonText(applyProductLlmAssistantPrefill(content, assistantPrefill), false);
                }
            }
        }

        if (readResult.done) {
            break;
        }
    }

    scheduleCompletedPartialProductLlmJsonText(applyProductLlmAssistantPrefill(content || rawResponseText, assistantPrefill), true);

    var finalContent = applyProductLlmAssistantPrefill(content || getProductLlmAiResponseContent(safeJsonParse(rawResponseText)) || rawResponseText, assistantPrefill);
    var jsonText = extractJsonObjectText(finalContent);

    if (!jsonText || jsonText === finalContent.trim() && !finalContent.trim().startsWith("{") && !finalContent.trim().startsWith("\`\`\`")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    self.postMessage({ type: "done", jsonText: jsonText });
}

function scheduleCompletedPartialProductLlmJsonText(content, force) {
    var partialJsonText = getCompletedPartialProductLlmJsonText(content);

    if (!partialJsonText) {
        return;
    }

    pendingPartialJsonText = partialJsonText;

    if (force) {
        if (partialPostTimer !== null) {
            clearTimeout(partialPostTimer);
            partialPostTimer = null;
        }

        postPendingPartialProductLlmJsonText();
        return;
    }

    if (partialPostTimer !== null) {
        return;
    }

    partialPostTimer = setTimeout(function () {
        partialPostTimer = null;
        postPendingPartialProductLlmJsonText();
    }, ${productLlmAiPartialApplyIntervalMs});
}

function postPendingPartialProductLlmJsonText() {
    if (!pendingPartialJsonText || pendingPartialJsonText === lastPostedPartialJsonText) {
        return;
    }

    lastPostedPartialJsonText = pendingPartialJsonText;
    self.postMessage({ type: "partial", partialJsonText: pendingPartialJsonText });
}

function normalizeProductLlmAiAssistantPrefill(assistantPrefill) {
    return String(assistantPrefill || "").trim();
}

function applyProductLlmAssistantPrefill(content, assistantPrefill) {
    var normalizedPrefill = normalizeProductLlmAiAssistantPrefill(assistantPrefill);
    var trimmedContent = String(content || "").trim();

    if (!normalizedPrefill || !trimmedContent) {
        return trimmedContent || normalizedPrefill;
    }

    if (trimmedContent.indexOf(normalizedPrefill) === 0 || trimmedContent.indexOf("{") === 0) {
        return trimmedContent;
    }

    return normalizedPrefill + trimmedContent;
}

function buildProductLlmAiMessages(args) {
    var promptText = args.promptText || "";
    var attachmentParts = getProductLlmAiAttachmentMessageParts(args.attachments || []);
    var messages = [
        {
            role: "system",
            content: args.systemPrompt
        },
        {
            role: "user",
            content: attachmentParts.length > 0
                ? attachmentParts.concat([{ type: "text", text: promptText }])
                : promptText
        }
    ];

    if (args.assistantPrefill) {
        messages.push({
            role: "assistant",
            content: args.assistantPrefill
        });
    }

    return messages;
}

function getProductLlmAiAttachmentMessageParts(attachments) {
    return (attachments || []).reduce(function (parts, attachment) {
        if (attachment && attachment.kind === "pdf" && attachment.dataUrl) {
            parts.push({
                type: "file",
                file: {
                    filename: attachment.fileName,
                    file_data: attachment.dataUrl
                }
            });
        }

        if (attachment && attachment.kind === "image" && attachment.dataUrl) {
            parts.push({
                type: "image_url",
                image_url: {
                    url: attachment.dataUrl,
                    detail: "auto"
                }
            });
        }

        return parts;
    }, []);
}

function getProductLlmJsonTextFromResponseText(responseBodyText, assistantPrefill) {
    var responseBody = safeJsonParse(responseBodyText);
    var content = applyProductLlmAssistantPrefill(getProductLlmAiResponseContent(responseBody) || responseBodyText, assistantPrefill);
    var jsonText = extractJsonObjectText(content);

    if (!jsonText || jsonText === content.trim() && !content.trim().startsWith("{") && !content.trim().startsWith("\`\`\`")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function getProductLlmAiStreamDeltaContent(responseBody) {
    var choice = responseBody && responseBody.choices && responseBody.choices[0];

    if (choice && choice.delta && typeof choice.delta.content === "string") {
        return choice.delta.content;
    }

    if (choice && choice.delta && Array.isArray(choice.delta.content)) {
        return choice.delta.content.map(function (contentPart) {
            return (contentPart && (contentPart.text || contentPart.content)) || "";
        }).join("");
    }

    if (responseBody && typeof responseBody.delta === "string") {
        return responseBody.delta;
    }

    if (responseBody && typeof responseBody.output_text_delta === "string") {
        return responseBody.output_text_delta;
    }

    if (responseBody && responseBody.type === "response.output_text.delta" && typeof responseBody.delta === "string") {
        return responseBody.delta;
    }

    return "";
}

function getProductLlmAiResponseContent(responseBody) {
    var chatCompletionContent = responseBody && responseBody.choices && responseBody.choices[0] && responseBody.choices[0].message && responseBody.choices[0].message.content;

    if (typeof chatCompletionContent === "string") {
        return chatCompletionContent;
    }

    if (Array.isArray(chatCompletionContent)) {
        return chatCompletionContent.map(function (contentPart) {
            return (contentPart && (contentPart.text || contentPart.content)) || "";
        }).join("\n").trim();
    }

    var responseOutputText = responseBody && responseBody.output_text;

    if (typeof responseOutputText === "string") {
        return responseOutputText;
    }

    return null;
}

function getProductLlmApiErrorMessage(status, responseBodyText) {
    var parsedBody = safeJsonParse(responseBodyText);
    var apiMessage = parsedBody && parsedBody.error && parsedBody.error.message || parsedBody && parsedBody.message;

    if (apiMessage) {
        return "AI request failed (" + status + "): " + apiMessage;
    }

    return "AI request failed (" + status + ").";
}

function safeJsonParse(text) {
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

function getCompletedPartialProductLlmJsonText(text) {
    var candidateText = getProductLlmJsonCandidateText(text);
    var valuesBody = getPartialObjectBodyByKey(candidateText, "values");

    if (valuesBody !== null) {
        var valuesPairs = extractCompletedProductLlmObjectPairs(valuesBody);

        if (valuesPairs.length > 0) {
            return "{\"values\":{" + valuesPairs.join(",") + "}}";
        }
    }

    var firstBraceIndex = candidateText.indexOf("{");

    if (firstBraceIndex === -1) {
        return null;
    }

    var topLevelPairs = extractCompletedProductLlmObjectPairs(candidateText.slice(firstBraceIndex + 1));

    if (topLevelPairs.length === 0) {
        return null;
    }

    return "{" + topLevelPairs.join(",") + "}";
}

function getProductLlmJsonCandidateText(text) {
    var trimmedText = text.trim();
    var fencedStartMatch = trimmedText.match(/\`\`\`(?:json)?\s*([\s\S]*)$/i);

    return ((fencedStartMatch && fencedStartMatch[1]) || trimmedText).trim();
}

function getPartialObjectBodyByKey(text, key) {
    var keyMatch = new RegExp("[\"']?" + escapeRegExp(key) + "[\"']?\\s*:\\s*\\{", "i").exec(text);

    if (!keyMatch || keyMatch.index === undefined) {
        return null;
    }

    var bodyStartIndex = keyMatch.index + keyMatch[0].length;
    return text.slice(bodyStartIndex);
}

function extractCompletedProductLlmObjectPairs(input) {
    var parts = splitProductLlmTopLevelByComma(input);
    var usableParts = input.trimEnd().endsWith(",") ? parts : parts.slice(0, -1);

    return usableParts.map(normalizeProductLlmObjectPair).filter(function (pair) {
        return Boolean(pair);
    });
}

function splitProductLlmTopLevelByComma(input) {
    var parts = [];
    var start = 0;
    var depth = 0;
    var inString = false;
    var escaped = false;

    for (var i = 0; i < input.length; i++) {
        var char = input[i];

        if (inString) {
            if (escaped) escaped = false;
            else if (char === "\\") escaped = true;
            else if (char === "\"") inString = false;
            continue;
        }

        if (char === "\"") inString = true;
        else if (char === "{" || char === "[") depth++;
        else if (char === "}" || char === "]") depth = Math.max(0, depth - 1);
        else if (char === "," && depth === 0) {
            parts.push(input.slice(start, i).trim());
            start = i + 1;
        }
    }

    parts.push(input.slice(start).trim());
    return parts;
}

function normalizeProductLlmObjectPair(pair) {
    var colonIndex = findProductLlmTopLevelColon(pair);

    if (colonIndex === -1) {
        return null;
    }

    var rawKey = pair.slice(0, colonIndex).trim();
    var rawValue = pair.slice(colonIndex + 1).trim();

    if (!rawKey || !rawValue) {
        return null;
    }

    var key = normalizeProductLlmJsonString(rawKey);
    var value = normalizeProductLlmJsonValue(rawValue);

    if (!key || value === null) {
        return null;
    }

    return key + ":" + value;
}

function findProductLlmTopLevelColon(input) {
    var depth = 0;
    var inString = false;
    var escaped = false;

    for (var i = 0; i < input.length; i++) {
        var char = input[i];

        if (inString) {
            if (escaped) escaped = false;
            else if (char === "\\") escaped = true;
            else if (char === "\"") inString = false;
            continue;
        }

        if (char === "\"") inString = true;
        else if (char === "{" || char === "[") depth++;
        else if (char === "}" || char === "]") depth = Math.max(0, depth - 1);
        else if (char === ":" && depth === 0) return i;
    }

    return -1;
}

function normalizeProductLlmJsonString(value) {
    var trimmed = value.trim().replace(/^,+|,+$/g, "");

    if (!trimmed) {
        return null;
    }

    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed;
    }

    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
        return JSON.stringify(trimmed.slice(1, -1));
    }

    return JSON.stringify(trimmed.replace(/^\"|\"$/g, ""));
}

function normalizeProductLlmJsonValue(value) {
    var trimmed = value.trim().replace(/,$/, "");

    if (!trimmed) {
        return null;
    }

    if (trimmed === "true" || trimmed === "false" || trimmed === "null") {
        return trimmed;
    }

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return trimmed;
    }

    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed;
    }

    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
        return JSON.stringify(trimmed.slice(1, -1));
    }

    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
            JSON.parse(trimmed);
            return trimmed;
        } catch (_) {
            return null;
        }
    }

    return JSON.stringify(trimmed.replace(/^\"|\"$/g, ""));
}

function escapeRegExp(value) {
    return String(value).replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/[{}]/g, "\\$&");
}

function extractJsonObjectText(text) {
    var firstBraceIndex = text.indexOf("{");
    var lastBraceIndex = text.lastIndexOf("}");

    if (firstBraceIndex === -1 || lastBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) {
        return text;
    }

    return text.slice(firstBraceIndex, lastBraceIndex + 1);
}
`;

    productLlmAiWorkerUrl = URL.createObjectURL(new Blob([workerSource], { type: "text/javascript" }));
    return productLlmAiWorkerUrl;
}

function isProductLlmOpenAiApiUrl(url?: string | null) {
    return /(^https?:\/\/)?api\.openai\.com\//i.test(url ?? "");
}

function resolveProductLlmAiOutputFormat(config: ProductLlmAiConfig): Exclude<ProductLlmAiOutputFormat, "auto"> {
    if (config.outputFormat === "json_object" || config.outputFormat === "text") {
        return config.outputFormat;
    }

    return isProductLlmOpenAiApiUrl(config.url) ? "json_object" : "text";
}

function resolveProductLlmAiAssistantPrefill(config: ProductLlmAiConfig) {
    if (config.assistantPrefillMode === "never") {
        return "";
    }

    if (config.assistantPrefillMode === "always") {
        return normalizeProductLlmAiAssistantPrefill(config.assistantPrefill);
    }

    return isProductLlmOpenAiApiUrl(config.url) ? "" : normalizeProductLlmAiAssistantPrefill(config.assistantPrefill);
}

function getProductLlmAiTemperature(value?: string | null) {
    const normalizedValue = String(value ?? "").trim();

    if (!normalizedValue) {
        return null;
    }

    const temperature = Number(normalizedValue);

    if (!Number.isFinite(temperature) || temperature < 0 || temperature > 2) {
        throw new Error("Temperature must be empty or a number from 0 to 2.");
    }

    return temperature;
}

function getProductLlmAiRequestBody(config: ProductLlmAiConfig, defaultModel: string, messages: any[]) {
    const isOpenAiApiUrl = isProductLlmOpenAiApiUrl(config.url);
    const promptCacheKey = config.promptCacheKey.trim();
    const promptCacheRetention = config.promptCacheRetention.trim();
    const temperature = getProductLlmAiTemperature(config.temperature);
    const outputFormat = resolveProductLlmAiOutputFormat(config);
    const requestBody: any = {
        model: config.model || defaultModel,
        messages,
        stream: true,
    };

    if (temperature !== null) {
        requestBody.temperature = temperature;
    }

    if (outputFormat === "json_object") {
        requestBody.response_format = { type: "json_object" };
    }

    if (isOpenAiApiUrl) {
        if (promptCacheKey) {
            requestBody.prompt_cache_key = promptCacheKey;
        }

        if (promptCacheRetention) {
            requestBody.prompt_cache_retention = promptCacheRetention;
        }
    } else {
        requestBody.cache_prompt = true;
    }

    return requestBody;
}

async function requestProductLlmAiResponseOnMainThread({
    config,
    userPrompt,
    formPrompt,
    attachments = [],
    onPartialJsonText,
}: ProductLlmAiRequestArgs) {
    const assistantPrefill = resolveProductLlmAiAssistantPrefill(config);
    const outputFormat = resolveProductLlmAiOutputFormat(config);
    const messages = buildProductLlmAiMessages({
        systemPrompt: getProductLlmAiSystemPrompt(assistantPrefill, outputFormat),
        promptText: buildProductLlmAiPrompt(userPrompt, formPrompt, attachments),
        attachments,
        assistantPrefill,
    });

    const response = await fetch(config.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(config.apiKey ? { Authorization: `Bearer ${config.apiKey}` } : {}),
        },
        body: JSON.stringify(getProductLlmAiRequestBody(config, productLlmAiDefaultConfig.model, messages)),
    });

    if (!response.ok) {
        const responseBodyText = await response.text();
        throw new Error(getProductLlmApiErrorMessage(response.status, responseBodyText));
    }

    if (!response.body) {
        const responseBodyText = await response.text();
        return getProductLlmJsonTextFromResponseText(responseBodyText, assistantPrefill);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let rawResponseText = "";
    let content = "";
    let sseBuffer = "";
    let chunksSinceYield = 0;

    while (true) {
        const { value, done } = await reader.read();
        const chunkText = decoder.decode(value ?? new Uint8Array(), { stream: !done });

        if (chunkText) {
            chunksSinceYield++;
            rawResponseText += chunkText;
            sseBuffer += chunkText;

            const lines = sseBuffer.split(/\r?\n/);
            sseBuffer = lines.pop() ?? "";

            for (const line of lines) {
                const trimmedLine = line.trim();

                if (!trimmedLine || !trimmedLine.startsWith("data:")) {
                    continue;
                }

                const data = trimmedLine.slice(5).trim();

                if (!data || data === "[DONE]") {
                    continue;
                }

                const parsedData = safeJsonParse(data);
                const deltaText = getProductLlmAiStreamDeltaContent(parsedData);

                if (deltaText) {
                    content += deltaText;

                    const partialJsonText = getCompletedPartialProductLlmJsonText(applyProductLlmAssistantPrefill(content, assistantPrefill));

                    if (partialJsonText) {
                        onPartialJsonText?.(partialJsonText);
                    }
                }
            }

            if (!rawResponseText.includes("data:")) {
                const responseBody = safeJsonParse(rawResponseText);
                const nonStreamContent = getProductLlmAiResponseContent(responseBody) ?? rawResponseText;

                if (nonStreamContent) {
                    content = nonStreamContent;
                    const partialJsonText = getCompletedPartialProductLlmJsonText(applyProductLlmAssistantPrefill(content, assistantPrefill));

                    if (partialJsonText) {
                        onPartialJsonText?.(partialJsonText);
                    }
                }
            }
        }

        if (chunksSinceYield >= 2) {
            chunksSinceYield = 0;
            await yieldProductLlmStreamToBrowser();
        }

        if (done) {
            break;
        }
    }

    const finalContent = applyProductLlmAssistantPrefill(content || getProductLlmAiResponseContent(safeJsonParse(rawResponseText)) || rawResponseText, assistantPrefill);
    const jsonText = extractJsonObjectText(finalContent);

    if (!jsonText || jsonText === finalContent.trim() && !finalContent.trim().startsWith("{") && !finalContent.trim().startsWith("```")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function getProductLlmJsonTextFromResponseText(responseBodyText: string, assistantPrefill?: string | null) {
    const responseBody = safeJsonParse(responseBodyText);
    const content = applyProductLlmAssistantPrefill(getProductLlmAiResponseContent(responseBody) ?? responseBodyText, assistantPrefill);
    const jsonText = extractJsonObjectText(content);

    if (!jsonText || jsonText === content.trim() && !content.trim().startsWith("{") && !content.trim().startsWith("```")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function yieldProductLlmStreamToBrowser() {
    return new Promise<void>((resolve) => window.setTimeout(resolve, 0));
}

function getProductLlmAiStreamDeltaContent(responseBody: any) {
    const choice = responseBody?.choices?.[0];

    if (typeof choice?.delta?.content === "string") {
        return choice.delta.content;
    }

    if (Array.isArray(choice?.delta?.content)) {
        return choice.delta.content.map((contentPart: any) => contentPart?.text ?? contentPart?.content ?? "").join("");
    }

    if (typeof responseBody?.delta === "string") {
        return responseBody.delta;
    }

    if (typeof responseBody?.output_text_delta === "string") {
        return responseBody.output_text_delta;
    }

    if (responseBody?.type === "response.output_text.delta" && typeof responseBody?.delta === "string") {
        return responseBody.delta;
    }

    return "";
}

function buildProductLlmAiPrompt(userPrompt: string, formPrompt: string, attachments: ProductLlmAiAttachment[] = []) {
    const attachmentPrompt = buildProductLlmAiAttachmentPrompt(attachments);
    const taskInstruction = userPrompt.trim() || "Use the attached files/images to update the product form.";

    return [
        "REFERENCE CONTEXT AND OUTPUT CONTRACT",
        "Use the following form context, field definitions, options, and JSON return rules to understand what values are valid.",
        "This reference context does not itself define the task for this run.",
        "",
        formPrompt,
        ...(attachmentPrompt ? [
            "",
            "ATTACHED SOURCE MATERIAL",
            "Use these attachments as evidence when carrying out the task-specific instruction below.",
            "",
            attachmentPrompt,
        ] : []),
        "",
        "============================================================",
        "TASK-SPECIFIC INSTRUCTION FOR THIS RUN",
        "============================================================",
        "The text below is the unique and important instruction supplied for this request.",
        "Carry it out directly using the reference context and attachments above.",
        "Do not treat it as background information, an example, or optional commentary.",
        "When it requests a form update, return every editable field that can reasonably be inferred and do not return an empty values object merely because some fields are uncertain.",
        "",
        taskInstruction,
        "",
        "============================================================",
        "END TASK-SPECIFIC INSTRUCTION",
        "============================================================",
    ].join("\n");
}


function buildProductLlmAiNonEmptyRetryPrompt(userPrompt: string, previousResponseText: string) {
    const taskInstruction = userPrompt.trim() || "Use the attached files/images to update the product form.";

    return [
        "============================================================",
        "TASK-SPECIFIC INSTRUCTION FOR THIS RUN",
        "============================================================",
        "This is the unique instruction that must be carried out:",
        "",
        taskInstruction,
        "",
        "============================================================",
        "CORRECTIVE RETRY REQUIREMENT",
        "============================================================",
        "Your previous response contained no editable Product fields:",
        previousResponseText,
        "Re-evaluate the task-specific instruction, attachments, current values, initial values, field requirements, and fieldOptions.",
        "Return a complete JSON object in the shape { \"values\": { ... } } containing every editable field that can reasonably be inferred or populated for this specific task.",
        "The values object must contain at least one editable Formik field unless the task explicitly requests no changes or the supplied material contains no usable product information at all.",
        "Do not abandon the entire task because one or more fields are uncertain. Omit only the uncertain fields.",
        "Do not explain the retry and do not return prose.",
    ].join("\n");
}

function hasProductLlmEditableFieldsInResponse(responseText: string) {
    try {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const nextValues = getFormikValuesFromLlmResponse(parsedResponse);

        return productLlmEditableFields.some((fieldName) => Object.prototype.hasOwnProperty.call(nextValues ?? {}, fieldName));
    } catch (_) {
        return false;
    }
}

function buildProductLlmAiAttachmentPrompt(attachments: ProductLlmAiAttachment[]) {
    if (attachments.length === 0) {
        return "";
    }

    return attachments.map((attachment, index) => {
        const header = `[Attachment ${index + 1}: ${attachment.fileName}; type=${attachment.mimeType || "unknown"}; size=${formatProductLlmAiBytes(attachment.size)}]`;

        if (attachment.kind === "text") {
            return [
                header,
                attachment.truncated ? `[Text content truncated to ${productLlmAiTextAttachmentMaxChars} characters.]` : "[Text content]",
                attachment.text ?? "",
            ].join("\n");
        }

        if (attachment.kind === "pdf") {
            return `${header}\n[PDF is attached as a file input. Read the PDF text and page images before deciding product form values.]`;
        }

        if (attachment.kind === "image") {
            return `${header}\n[Image is attached to the multimodal user message. Use visible labels, product IDs, dimensions, packaging text, and other image details when relevant.]`;
        }

        return `${header}\n[Attachment not included: ${attachment.omittedReason ?? "Unsupported file type."}]`;
    }).join("\n\n");
}

function buildProductLlmAiMessages({
    systemPrompt,
    promptText,
    attachments = [],
    assistantPrefill,
}: {
    systemPrompt: string;
    promptText: string;
    attachments?: ProductLlmAiAttachment[];
    assistantPrefill?: string;
}): ProductLlmAiChatMessage[] {
    const attachmentParts = getProductLlmAiAttachmentMessageParts(attachments);
    const messages: ProductLlmAiChatMessage[] = [
        {
            role: "system",
            content: systemPrompt,
        },
        {
            role: "user",
            content: attachmentParts.length > 0
                ? [
                    ...attachmentParts,
                    { type: "text", text: promptText },
                ]
                : promptText,
        },
    ];

    if (assistantPrefill) {
        messages.push({ role: "assistant", content: assistantPrefill });
    }

    return messages;
}
function getProductLlmAiAttachmentMessageParts(attachments: ProductLlmAiAttachment[]) {
    return attachments.reduce((parts: ProductLlmAiMessageContentPart[], attachment) => {
        if (attachment.kind === "pdf" && attachment.dataUrl) {
            parts.push({
                type: "file",
                file: {
                    filename: attachment.fileName,
                    file_data: attachment.dataUrl,
                },
            });
        }

        if (attachment.kind === "image" && attachment.dataUrl) {
            parts.push({
                type: "image_url",
                image_url: {
                    url: attachment.dataUrl,
                    detail: "auto",
                },
            });
        }

        return parts;
    }, []);
}

function getProductLlmAiResponseContent(responseBody: any) {
    const chatCompletionContent = responseBody?.choices?.[0]?.message?.content;

    if (typeof chatCompletionContent === "string") {
        return chatCompletionContent;
    }

    if (Array.isArray(chatCompletionContent)) {
        return chatCompletionContent.map((contentPart) => contentPart?.text ?? contentPart?.content ?? "").join("\n").trim();
    }

    const responseOutputText = responseBody?.output_text;

    if (typeof responseOutputText === "string") {
        return responseOutputText;
    }

    return null;
}

function getProductLlmApiErrorMessage(status: number, responseBodyText: string) {
    const parsedBody = safeJsonParse(responseBodyText);
    const apiMessage = parsedBody?.error?.message ?? parsedBody?.message;

    if (apiMessage) {
        return `AI request failed (${status}): ${apiMessage}`;
    }

    return `AI request failed (${status}).`;
}

function safeJsonParse(text: string) {
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

async function readProductLlmAiAttachmentsFromFiles(files: File[]) {
    return Promise.all(files.map(readProductLlmAiAttachmentFromFile));
}

async function readProductLlmAiAttachmentFromFile(file: File): Promise<ProductLlmAiAttachment> {
    const baseAttachment = {
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
    };

    if (isProductLlmAiPdfFile(file)) {
        if (file.size > productLlmAiPdfAttachmentMaxBytes) {
            return {
                ...baseAttachment,
                kind: "unsupported",
                omittedReason: `PDF is larger than ${formatProductLlmAiBytes(productLlmAiPdfAttachmentMaxBytes)}.`,
            };
        }

        return {
            ...baseAttachment,
            kind: "pdf",
            dataUrl: await readProductLlmAiFileAsDataUrl(file),
        };
    }

    if (isProductLlmAiImageFile(file)) {
        if (file.size > productLlmAiImageAttachmentMaxBytes) {
            return {
                ...baseAttachment,
                kind: "unsupported",
                omittedReason: `Image is larger than ${formatProductLlmAiBytes(productLlmAiImageAttachmentMaxBytes)}.`,
            };
        }

        return {
            ...baseAttachment,
            kind: "image",
            dataUrl: await readProductLlmAiFileAsDataUrl(file),
        };
    }

    if (isProductLlmAiTextFile(file)) {
        const fullText = await readProductLlmAiFileAsText(file);
        const truncated = fullText.length > productLlmAiTextAttachmentMaxChars;
        const text = truncated ? fullText.slice(0, productLlmAiTextAttachmentMaxChars) : fullText;

        return {
            ...baseAttachment,
            kind: "text",
            text,
            truncated,
        };
    }

    return {
        ...baseAttachment,
        kind: "unsupported",
        omittedReason: "Only PDFs, images, and text-like files are currently included in AI prompts.",
    };
}

function isProductLlmAiPdfFile(file: File) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return mimeType === "application/pdf" || mimeType === "application/x-pdf" || fileName.endsWith(".pdf");
}

function isProductLlmAiImageFile(file: File) {
    return file.type.toLowerCase().startsWith("image/");
}

function isProductLlmAiTextFile(file: File) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return productLlmAiTextAttachmentMimeTypes.some((acceptedMimeType) => mimeType.startsWith(acceptedMimeType)) ||
        productLlmAiTextAttachmentExtensions.some((extension) => fileName.endsWith(extension));
}

function readProductLlmAiFileAsText(file: File) {
    if (typeof file.text === "function") {
        return file.text();
    }

    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Could not read text attachment."));
        reader.readAsText(file);
    });
}

function readProductLlmAiFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Could not read attachment."));
        reader.readAsDataURL(file);
    });
}

function getProductLlmAiAttachmentChipLabel(attachment: ProductLlmAiAttachment) {
    const suffix = attachment.kind === "text" && attachment.truncated ? ", truncated" : attachment.kind === "unsupported" ? ", unsupported" : "";
    return `${attachment.fileName} (${attachment.kind}, ${formatProductLlmAiBytes(attachment.size)}${suffix})`;
}

function formatProductLlmAiBytes(bytes: number) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return "0 B";
    }

    const units = ["B", "KB", "MB", "GB"];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex++;
    }

    return `${value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
}

async function writeTextToClipboard(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    if (typeof document === "undefined") {
        throw new Error("Clipboard write is not available.");
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();

    const success = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (!success) {
        throw new Error("Fallback clipboard copy failed.");
    }
}

function parseJsonFromClipboardText(clipboardText: string) {
    const trimmedText = clipboardText.trim();
    const fencedJsonMatch = trimmedText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const jsonText = fencedJsonMatch?.[1] ?? extractJsonObjectText(trimmedText);

    try {
        return JSON.parse(jsonText);
    } catch (error) {
        const partialJsonText = getCompletedPartialProductLlmJsonText(jsonText);

        if (!partialJsonText) {
            throw error;
        }

        return JSON.parse(partialJsonText);
    }
}

function getCompletedPartialProductLlmJsonText(text: string) {
    const candidateText = getProductLlmJsonCandidateText(text);
    const valuesBody = getPartialObjectBodyByKey(candidateText, "values");

    if (valuesBody !== null) {
        const valuesPairs = extractCompletedProductLlmObjectPairs(valuesBody);

        if (valuesPairs.length > 0) {
            return `{"values":{${valuesPairs.join(",")}}}`;
        }
    }

    const firstBraceIndex = candidateText.indexOf("{");

    if (firstBraceIndex === -1) {
        return null;
    }

    const topLevelPairs = extractCompletedProductLlmObjectPairs(candidateText.slice(firstBraceIndex + 1));

    if (topLevelPairs.length === 0) {
        return null;
    }

    return `{${topLevelPairs.join(",")}}`;
}

function getProductLlmJsonCandidateText(text: string) {
    const trimmedText = text.trim();
    const fencedStartMatch = trimmedText.match(/```(?:json)?\s*([\s\S]*)$/i);

    return (fencedStartMatch?.[1] ?? trimmedText).trim();
}

function getPartialObjectBodyByKey(text: string, key: string) {
    const keyMatch = new RegExp(`["']?${escapeRegExp(key)}["']?\\s*:\\s*\\{`, "i").exec(text);

    if (!keyMatch || keyMatch.index === undefined) {
        return null;
    }

    const bodyStartIndex = keyMatch.index + keyMatch[0].length;
    return text.slice(bodyStartIndex);
}

function extractCompletedProductLlmObjectPairs(input: string) {
    const parts = splitProductLlmTopLevelByComma(input);
    const usableParts = input.trimEnd().endsWith(",") ? parts : parts.slice(0, -1);

    return usableParts
        .map(normalizeProductLlmObjectPair)
        .filter((pair): pair is string => Boolean(pair));
}

function splitProductLlmTopLevelByComma(input: string) {
    const parts: string[] = [];
    let start = 0;
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inString) {
            if (escaped) escaped = false;
            else if (char === "\\") escaped = true;
            else if (char === "\"") inString = false;
            continue;
        }

        if (char === "\"") inString = true;
        else if (char === "{" || char === "[") depth++;
        else if (char === "}" || char === "]") depth = Math.max(0, depth - 1);
        else if (char === "," && depth === 0) {
            parts.push(input.slice(start, i).trim());
            start = i + 1;
        }
    }

    parts.push(input.slice(start).trim());
    return parts;
}

function normalizeProductLlmObjectPair(pair: string) {
    const colonIndex = findProductLlmTopLevelColon(pair);

    if (colonIndex === -1) {
        return null;
    }

    const rawKey = pair.slice(0, colonIndex).trim();
    const rawValue = pair.slice(colonIndex + 1).trim();

    if (!rawKey || !rawValue) {
        return null;
    }

    const key = normalizeProductLlmJsonString(rawKey);
    const value = normalizeProductLlmJsonValue(rawValue);

    if (!key || value === null) {
        return null;
    }

    return `${key}:${value}`;
}

function findProductLlmTopLevelColon(input: string) {
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inString) {
            if (escaped) escaped = false;
            else if (char === "\\") escaped = true;
            else if (char === "\"") inString = false;
            continue;
        }

        if (char === "\"") inString = true;
        else if (char === "{" || char === "[") depth++;
        else if (char === "}" || char === "]") depth = Math.max(0, depth - 1);
        else if (char === ":" && depth === 0) return i;
    }

    return -1;
}

function normalizeProductLlmJsonString(value: string) {
    const trimmed = value.trim().replace(/^,+|,+$/g, "");

    if (!trimmed) {
        return null;
    }

    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed;
    }

    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
        return JSON.stringify(trimmed.slice(1, -1));
    }

    return JSON.stringify(trimmed.replace(/^\"|\"$/g, ""));
}

function normalizeProductLlmJsonValue(value: string) {
    const trimmed = value.trim().replace(/,$/, "");

    if (!trimmed) {
        return null;
    }

    if (trimmed === "true" || trimmed === "false" || trimmed === "null") {
        return trimmed;
    }

    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return trimmed;
    }

    if (trimmed.startsWith("\"") && trimmed.endsWith("\"")) {
        return trimmed;
    }

    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
        return JSON.stringify(trimmed.slice(1, -1));
    }

    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
            JSON.parse(trimmed);
            return trimmed;
        } catch (_) {
            return null;
        }
    }

    return JSON.stringify(trimmed.replace(/^\"|\"$/g, ""));
}

function escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractJsonObjectText(text: string) {
    const firstBraceIndex = text.indexOf("{");
    const lastBraceIndex = text.lastIndexOf("}");

    if (firstBraceIndex === -1 || lastBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) {
        return text;
    }

    return text.slice(firstBraceIndex, lastBraceIndex + 1);
}

function getFormikValuesFromLlmResponse(parsedResponse: any) {
    const unwrappedResponse = unwrapProductLlmResponseValue(parsedResponse);

    if (unwrappedResponse?.values && typeof unwrappedResponse.values === "object" && !Array.isArray(unwrappedResponse.values)) {
        return unwrappedResponse.values;
    }

    if (unwrappedResponse?.formValues && typeof unwrappedResponse.formValues === "object" && !Array.isArray(unwrappedResponse.formValues)) {
        return unwrappedResponse.formValues;
    }

    for (const wrapperKey of ["result", "data", "output", "response", "json"]) {
        const wrapperValue = unwrapProductLlmResponseValue(unwrappedResponse?.[wrapperKey]);

        if (wrapperValue && typeof wrapperValue === "object" && !Array.isArray(wrapperValue)) {
            const nestedValues = getFormikValuesFromLlmResponse(wrapperValue);

            if (nestedValues && typeof nestedValues === "object" && !Array.isArray(nestedValues)) {
                return nestedValues;
            }
        }
    }

    return unwrappedResponse;
}

function unwrapProductLlmResponseValue(value: any) {
    if (typeof value !== "string") {
        return value;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return value;
    }

    try {
        return JSON.parse(trimmedValue);
    } catch (_) {
        return value;
    }
}

function getProductLlmAiResponseDebugSummary(responseText: string) {
    try {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const unwrappedValues = getFormikValuesFromLlmResponse(parsedResponse);
        const topLevelKeys = parsedResponse && typeof parsedResponse === "object" && !Array.isArray(parsedResponse) ? Object.keys(parsedResponse) : [];
        const candidateKeys = unwrappedValues && typeof unwrappedValues === "object" && !Array.isArray(unwrappedValues) ? Object.keys(unwrappedValues) : [];
        const matchingFields = candidateKeys.filter((key) => productLlmEditableFields.includes(key));
        const ignoredFields = candidateKeys.filter((key) => !productLlmEditableFields.includes(key));

        return [
            `Top-level keys: ${topLevelKeys.length ? topLevelKeys.join(", ") : "none"}`,
            `Candidate form keys: ${candidateKeys.length ? candidateKeys.join(", ") : "none"}`,
            `Matching editable fields: ${matchingFields.length ? matchingFields.join(", ") : "none"}`,
            `Ignored/non-form keys: ${ignoredFields.length ? ignoredFields.join(", ") : "none"}`,
        ].join("\n");
    } catch (error) {
        return `The response could not be parsed as JSON: ${error instanceof Error ? error.message : "Unknown parsing error"}`;
    }
}

function getProductLlmAiResponseKeySummary(responseText: string) {
    try {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const candidateValues = getFormikValuesFromLlmResponse(parsedResponse);
        const candidateKeys = candidateValues && typeof candidateValues === "object" && !Array.isArray(candidateValues) ? Object.keys(candidateValues) : [];

        return candidateKeys.length > 0 ? `Returned candidate keys: ${candidateKeys.join(", ")}.` : "The parsed object contained no candidate field keys.";
    } catch (_) {
        return "Open the AI response panel to inspect the returned text.";
    }
}

function getAllowedFormikValues(nextValues: any, currentValues: any) {
    if (!nextValues || typeof nextValues !== "object" || Array.isArray(nextValues)) {
        return {};
    }

    return productLlmEditableFields.reduce((acc: any, fieldName) => {
        if (productLlmReadOnlyFields.includes(fieldName)) {
            return acc;
        }

        if (Object.prototype.hasOwnProperty.call(nextValues, fieldName)) {
            acc[fieldName] = nextValues[fieldName];
        }

        return acc;
    }, {});
}

function getChangedFormikValues(allowedValues: any, currentValues: any) {
    return Object.keys(allowedValues).reduce((acc: any, fieldName) => {
        const currentValue = getValueByPath(currentValues, fieldName);
        const nextValue = allowedValues[fieldName];

        if (!areProductLlmValuesEqual(currentValue, nextValue)) {
            acc[fieldName] = nextValue;
        }

        return acc;
    }, {});
}

function getNextProductLlmChangedFields(previousChangedFields: ProductLlmChangedFields, changedValues: any, currentValues: any) {
    const nextChangedFields: ProductLlmChangedFields = { ...previousChangedFields };

    Object.keys(changedValues).forEach((fieldName) => {
        const originalValue = previousChangedFields[fieldName]?.originalValue ?? getValueByPath(currentValues, fieldName);
        const pastedValue = changedValues[fieldName];

        if (areProductLlmValuesEqual(originalValue, pastedValue)) {
            delete nextChangedFields[fieldName];
            return;
        }

        nextChangedFields[fieldName] = {
            fieldName,
            originalValue,
            pastedValue,
        };
    });

    return nextChangedFields;
}

function getProductLlmDiffOptions(fieldProps: any, formikProps: FormikProps<any>, productLlmClipboard: ProductLlmClipboardController) {
    const fieldName = fieldProps?.name;
    const change = fieldName ? productLlmClipboard.changedFields[fieldName] : null;
    const currentValue = fieldName ? getValueByPath(formikProps.values, fieldName) : undefined;
    const isUnsupportedDiffField = Boolean(fieldProps.checkbox || fieldProps.date || fieldProps.disabled);
    const shouldShowDiff = Boolean(fieldName && change && !isUnsupportedDiffField && !areProductLlmValuesEqual(change.originalValue, currentValue));

    if (!shouldShowDiff) {
        return {};
    }

    return {
        llmDiffActive: true,
        llmDiffOriginalValue: change.originalValue,
        llmDiffFieldName: fieldName,
        llmDiffOnRevert: productLlmClipboard.revertField,
    };
}


function compactProductValues(values: any) {
    const sourceValues = values ?? {};

    return productLlmContextFields.reduce((acc: any, fieldName) => {
        if (Object.prototype.hasOwnProperty.call(sourceValues, fieldName)) {
            acc[fieldName] = sourceValues[fieldName];
        }

        return acc;
    }, {});
}

function compactProductLabelValueOptions(options: any) {
    if (!Array.isArray(options)) {
        return {};
    }

    return options.reduce((acc: Record<string, any>, option: any) => {
        const label = option?.label ?? option?.Value ?? option?.Name ?? option?.text;
        const value = option?.value ?? option?.Key ?? option?.Id ?? option?.id;

        if (label === undefined || label === null || value === undefined || value === null) {
            return acc;
        }

        acc[String(label)] = value;
        return acc;
    }, {});
}

function compactProductSubClassOptions(productSubClassOptions: any) {
    if (!productSubClassOptions) {
        return {};
    }

    if (Array.isArray(productSubClassOptions)) {
        return productSubClassOptions.reduce((acc: Record<string, any>, option: any) => {
            const parentId = option?.ParentId ?? option?.parentId ?? option?.ProductClass ?? option?.productClass;
            const label = option?.label ?? option?.Value ?? option?.Name ?? option?.text;
            const value = option?.value ?? option?.Key ?? option?.Id ?? option?.id;

            if (parentId === undefined || parentId === null || label === undefined || label === null || value === undefined || value === null) {
                return acc;
            }

            const parentKey = String(parentId);
            acc[parentKey] = acc[parentKey] ?? {};
            acc[parentKey][String(label)] = value;
            return acc;
        }, {});
    }

    if (typeof productSubClassOptions !== "object") {
        return {};
    }

    return Object.keys(productSubClassOptions).reduce((acc: Record<string, any>, productClassValue) => {
        acc[productClassValue] = compactProductLabelValueOptions(productSubClassOptions[productClassValue]);
        return acc;
    }, {});
}

function compactProductRelatedData(currentValues: any, initialValues: any) {
    return {
        SupplierProducts: compactProductSupplierProducts(getFirstArrayValue(currentValues?.SupplierProducts, initialValues?.SupplierProducts)),
        SalesItems: compactProductSalesItems(getFirstArrayValue(currentValues?.SalesItems, initialValues?.SalesItems)),
    };
}

function compactProductSupplierProducts(supplierProducts: any) {
    const rows = Array.isArray(supplierProducts) ? supplierProducts : [];
    const compactRows = rows.map((supplierProduct: any) => [
        supplierProduct?.SupplierProduct ?? supplierProduct?.SupplierProductId ?? supplierProduct?.ArticleNumber ?? "",
        supplierProduct?.SupplierProduct_Value ?? supplierProduct?.SupplierProductName ?? supplierProduct?.Name ?? "",
        supplierProduct?.SupplierName ?? supplierProduct?.Supplier?.Value ?? supplierProduct?.Supplier?.label ?? "",
        supplierProduct?.ManufacturerProduct ?? supplierProduct?.ManufacturerProductId ?? "",
        supplierProduct?.Price ?? "",
        supplierProduct?.Status?.Value ?? supplierProduct?.Status ?? supplierProduct?.SupplierProductStatus?.Value ?? "",
    ]).filter((row: any[]) => row.some((value) => value !== "" && value !== null && typeof value !== "undefined"));

    return {
        schema: ["SupplierProduct", "SupplierProduct_Value", "SupplierName", "ManufacturerProduct", "Price", "Status"],
        rowCount: compactRows.length,
        rows: compactRows.slice(0, 25),
    };
}

function compactProductSalesItems(salesItems: any) {
    const rows = Array.isArray(salesItems) ? salesItems : [];
    const compactRows = rows.map((salesItem: any) => [
        salesItem?.SalesItemId ?? "",
        salesItem?.ArticleNumber ?? "",
        salesItem?.SalesItemName ?? salesItem?.Name ?? "",
        salesItem?.SalesItemStatus?.Value ?? salesItem?.Status?.Value ?? salesItem?.Status ?? "",
        salesItem?.MainItem ?? "",
    ]).filter((row: any[]) => row.some((value) => value !== "" && value !== null && typeof value !== "undefined"));

    return {
        schema: ["SalesItemId", "ArticleNumber", "SalesItemName", "Status", "MainItem"],
        rowCount: compactRows.length,
        rows: compactRows.slice(0, 25),
    };
}

function getFirstArrayValue(...possibleArrays: any[]) {
    return possibleArrays.find((value) => Array.isArray(value)) ?? [];
}

function areProductLlmValuesEqual(firstValue: any, secondValue: any) {
    return normalizeProductLlmValue(firstValue) === normalizeProductLlmValue(secondValue);
}

function normalizeProductLlmValue(value: any) {
    if (value === undefined || value === null) {
        return "";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value).trim();
}

function formatProductLlmPreviewValue(value: any) {
    if (value === undefined || value === null || value === "") {
        return "Empty";
    }

    if (typeof value === "boolean") {
        return value ? "True" : "False";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

function getValueByPath(values: any, path: string) {
    return path.split(".").reduce((acc, pathPart) => acc?.[pathPart], values);
}

function getPageNameText(pageName: string | { text: string; after?: React.ReactNode }) {
    return typeof pageName === "string" ? pageName : pageName.text;
}

const toBoolean = (value: any) => {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return Boolean(value);
};

export default EditProductWithAiButtons;
